/**
 * HUD — toàn bộ giao diện ngoài sân đấu (DOM).
 * Hỗ trợ giao diện kép mượt mà cho cả Desktop và Mobile/App.
 * Dùng DOM vì chữ nét căng, dễ căn chỉnh tỷ lệ theo màn hình, dễ nâng cấp.
 */
import type { Game } from '../game';
import { CLASSES, CLASS_ORDER, type ClassId } from '../data/classes';
import { MAX_EQUIPPED_SKILLS, classOfSkill, craftableSkills, skillById, skillPool } from '../data/skills-extra';
import { DIAMOND_SHOP, type ShopItem, type ShopKind } from '../data/diamond-shop';
import { SHARD_COST, buyShopItem, craftSkill, equipSkill, equippedSkills, unequipSkill } from '../sim/craft';
import { DIFFICULTIES } from '../data/maps';
import { chromaKeyMagenta } from '../art/ai-parts';
import { applyStatic, getLang, onLangChange, setLang, t } from '../i18n';
void isLoading;
import { WAVE_TIP_EN } from '../i18n/content-en';
import { ultDesc, ultName, waveTip } from '../i18n/loc';
import { simMsg } from '../i18n/sim-messages';
import {
  className,
  classRole,
  classTitle,
  classBlurb,
  diffName,
  mapBlurb,
  mapName,
  potionDesc,
  potionShortName,
  skillDesc,
  skillName,
} from '../i18n/loc';
import { ULTIMATES } from '../data/ultimates';
import { mapArtSrc, upgradeMapArt } from './map-art';
import { beginTask, isLoading, setProgress } from './loading';
import { POTIONS, type PotionKind } from '../data/items';
import { formsForClass, skillFormById } from '../data/wardrobe';
import { artPartsUrl } from '../art/art-url';
import { MAX_ROSTER, SHARD_SELL_GOLD, UNLOCK_SHARDS, UPGRADE_SHARDS, classStatsFor, powerScore } from '../sim/meta';
import { PARTS_ATLAS } from '../art/parts';
import { MAX_HEROES_ON_FIELD, type World } from '../sim/world';
import type { Unit } from '../sim/types';
import { sound } from './sound';
import { ArenaHud, type ArenaSnapshot } from './hud-arena';

interface HeroCardRefs {
  root: HTMLElement;
  hp: HTMLElement;
  mana: HTMLElement;
  stam: HTMLElement;
  skills: HTMLButtonElement[];
  /**
   * TÊN của 7 ô chiêu (`.sk-name`) — song song theo CHỈ SỐ với `skills`.
   *
   * Vì sao phải giữ: `renderHeroCards()` chỉ dựng HTML ở nhánh `!refs` (thẻ đã có thì tái dùng), nên
   * nếu chỉ ghi tên lúc dựng thẻ thì bấm VI/EN xong TÊN CHIÊU vẫn nằm nguyên ngôn ngữ cũ — trong khi
   * `.sk-mana` (số) và `.cd` (vòng hồi) vẫn đúng. Xem `syncSkillNames()`.
   */
  skillNames: HTMLElement[];
  /**
   * NÚT ULTIMATE — 1 ô cho mỗi tướng, nằm DƯỚI dãy 7 ô chiêu.
   *
   * ⚠️ `name` cũng phải giữ tham chiếu dù nó chỉ đổi khi ĐỔI NGÔN NGỮ: thẻ tướng đã dựng thì
   * `renderHeroCards()` KHÔNG dựng lại HTML (chỉ nhánh `!refs` mới dựng) ⇒ nếu chỉ ghi tên 1 lần
   * lúc dựng thẻ thì bấm VI/EN xong tên ultimate vẫn nằm nguyên ngôn ngữ cũ.
   */
  ult?: { btn: HTMLButtonElement; name: HTMLElement; state: HTMLElement; cd: HTMLElement };
  ammo?: HTMLElement;
  status: HTMLElement;
}

// `Partial` vì 2 class mới (Thợ mộc · Thợ lặn) chưa có seal — và theo yêu cầu người dùng, chỗ này
// sẽ được THAY BẰNG ẢNH HEAD của class (xem `classHeadHtml`), nên chữ Hán chỉ còn là dự phòng.
const CLASS_SEALS: Partial<Record<ClassId, string>> = {
  strength: '力', // Dân tập gym (lực/ tạ)
  agility: '媚',  // Gái ngành (mị — thiếu nữ quyến rũ)
  street: '市',   // Dân chợ búa (chợ)
  army: '槍',     // Bắn thuê (súng)
  mother: '育',   // Đẻ mướn (sinh/ dưỡng)
};

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Thiếu phần tử #${id}`);
  return found as T;
}

/**
 * ẢNH HEAD CỦA CLASS — dùng THAY chữ Hán ở mọi chỗ cần "nhận diện class".
 *
 * Người dùng: *"trong màn hình gameplay, chỗ mua tướng cũng cho ảnh `Head` của class để dễ dàng
 * mua và nhận viết thay cho icon Tiếng Trung"*, và *"Thay các mảnh, item trong shop bằng mảnh ghép,
 * bên trong có `head` của class đó"*.
 *
 * Nguồn ảnh: `art/ai/<class>-parts.png` là lưới 3×3, **ô 1 = ĐẦU** (xem `src/art/ai-parts.ts`).
 * Không nạp cả lưới rồi cắt bằng canvas — chỉ cần HIỆN ĐÚNG Ô 1 nên dùng CSS:
 * `background-size: 300% 300%` + `background-position: 0 0` ⇒ lưới 3×3 phóng 300% thì ô 1 nằm đúng
 * ở góc trên-trái. 0 request JS thêm, 0 canvas, ảnh cache như mọi ảnh khác.
 *
 * ⚠️ LẦN ĐẦU TIÊN ảnh còn đang tải ⇒ hiện CON DẤU làm chỗ đứng rồi tự vẽ lại khi tải xong
 * (`onReady`), KHÔNG để ô trống. Class chưa có ảnh (2 class mới) thì giữ con dấu luôn.
 */
const classArtCache = new Map<string, boolean>();
/** Class đang dò ảnh + callback chờ (gộp nhiều yêu cầu cho cùng 1 ảnh). */
const pending = new Map<string, Array<() => void>>();
function classPartsUrl(classId: ClassId): string {
  // `artPartsUrl` đổi đuôi sang đuôi THẬT (`sync-art` nén `.webp`) — xem src/art/art-url.ts
  return artPartsUrl(classId);
}
function classArtReady(classId: ClassId): boolean {
  // "sẵn sàng" = đã có ẢNH HEAD ĐÃ TÁCH NỀN (không phải chỉ tải xong file gốc)
  return classHeadDataUrl.has(classId);
}
/**
 * Dò MỘT LẦN cho cả phiên, VÀ TÁCH NỀN MAGENTA ngay tại đây.
 *
 * ⚠️⚠️ PHÁT HIỆN QUAN TRỌNG (đo trên ảnh chụp phóng to 140px): hiện thẳng ô 1 của
 * `art/ai/<class>-parts.png` bằng `background-image` cho ra **NỀN HỒNG ĐẶC** — sheet bộ phận có
 * nền magenta `#FF00FF`, không phải nền trong suốt (đo pixel góc ô 1 = `rgb(251,3,247)`). Đó là lý
 * do ảnh head nhìn như bị bóp/ lệch: nửa ô là hồng.
 *
 * ⇒ Phải đi qua canvas: cắt ô 1 → `chromaKeyMagenta()` (dùng CHUNG hàm với lúc nạp sprite, không
 * viết lại thuật toán) → `toDataURL`. Kết quả cache vào `classHeadDataUrl` nên mỗi class chỉ xử lý
 * MỘT lần cho cả phiên.
 */
const classHeadDataUrl = new Map<string, string>();
/** Class ĐANG tải ảnh head + callback đang chờ (gộp nhiều yêu cầu cho cùng 1 ảnh). */
const probing = new Set<string>();
const pendingHead = new Map<string, Array<() => void>>();

/** Hộp bao của pixel KHÔNG trong suốt (`null` nếu cả ô trống). */
function alphaBBox(data: Uint8ClampedArray, w: number, h: number): { x0: number; y0: number; x1: number; y1: number } | null {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  return x1 < 0 ? null : { x0, y0, x1, y1 };
}
function probeClassArt(classId: ClassId, onReady: () => void): void {
  // đã có ảnh head đã xử lý ⇒ báo ngay
  if (classHeadDataUrl.has(classId)) {
    onReady();
    return;
  }
  /*
   * ⚠️⚠️ LỖI ĐÃ SỬA — ĐỌC KỸ TRƯỚC KHI SỬA: bản trước dùng `classArtCache.set(classId, false)` làm
   * cờ "đang dò", nhưng chính dòng `if (classArtCache.has(classId)) return` phía trên lại chặn mọi
   * lần gọi SAU ⇒ lần dò đầu bị nuốt, các lần sau thoát sớm ⇒ **ảnh KHÔNG BAO GIỜ được tải**, và
   * giao diện mãi hiện mảnh ghép rỗng (đo được: mọi ô head đều là ảnh GỐC còn nguyên nền hồng vì
   * canvas chưa từng chạy).
   * Nay tách hẳn 2 việc: `probing` = ĐANG tải (gộp callback), `classArtCache` = KẾT QUẢ (true/false).
   */
  if (probing.has(classId)) {
    pendingHead.get(classId)!.push(onReady);
    return;
  }
  if (classArtCache.get(classId) === false) return; // đã dò và CHẮC CHẮN không có ảnh
  probing.add(classId);
  pendingHead.set(classId, [onReady]);
  const im = new Image();
  im.onload = () => {
    if (im.naturalWidth < 300) return;
    try {
      const cell = Math.floor(Math.min(im.naturalWidth, im.naturalHeight) / 3);
      const cv = document.createElement('canvas');
      cv.width = cell;
      cv.height = cell;
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(im, 0, 0, cell, cell, 0, 0, cell, cell);
      const data = ctx.getImageData(0, 0, cell, cell);
      chromaKeyMagenta(data.data); // tách nền magenta — dùng chung với `art/ai-parts.ts`
      /*
       * CẮT SÁT NỘI DUNG (trim) trước khi xuất.
       *
       * ⚠️ VÌ SAO: đầu nhân vật KHÔNG lấp đầy ô lưới — ô 1 rộng 341px nhưng đầu chỉ chiếm ~1/2 và
       * nằm lệch. Đo trên ảnh chụp: hiện nguyên ô thì đầu bị nhỏ và trông như bị cắt mất tai/ tóc.
       * Trim theo hộp bao pixel KHÔNG trong suốt rồi mới xuất ⇒ đầu lấp đầy khung, nét và rõ.
       */
      /*
       * ĐƯA ĐẦU VÀO KHUNG VUÔNG SÁT HỘP BAO + PHÓNG ĐẦY KHUNG.
       *
       * ⚠️ LỖI ĐÃ SỬA: bản trước cắt từ `cv` (canvas 341×341 CÒN LỀ) theo hộp bao. Nhưng hộp bao
       * của ô 1 CHẠM MÉP trên (tóc/ đầu sát lề) nên `side` luôn = cạnh ô ⇒ **không trim được gì**,
       * kết quả vẫn còn lề hồng và đầu nhỏ tí (đo trên ảnh chụp: 5 ô vẫn nền hồng).
       * Nay: `putImageData` phần ĐÃ TÁCH NỀN, rồi vẽ lại với tỉ lệ phóng để đầu lấp đầy khung.
       */
      ctx.putImageData(data, 0, 0);
      const box = alphaBBox(data.data, cell, cell) ?? { x0: 0, y0: 0, x1: cell - 1, y1: cell - 1 };
      const bw = box.x1 - box.x0 + 1;
      const bh = box.y1 - box.y0 + 1;
      const side = Math.max(bw, bh);
      const out = document.createElement('canvas');
      out.width = side;
      out.height = side;
      const octx = out.getContext('2d');
      if (!octx) return;
      // vẽ đúng vùng hộp bao (đã tách nền) vào khung vuông `side`, canh giữa
      octx.drawImage(
        cv,
        box.x0,
        box.y0,
        bw,
        bh,
        (side - bw) / 2,
        (side - bh) / 2,
        bw,
        bh,
      );
      classHeadDataUrl.set(classId, out.toDataURL('image/png'));
      classArtCache.set(classId, true);
      onReady();
    } catch {
      // canvas bị chặn (CORS) ⇒ giữ mảnh ghép rỗng, KHÔNG vỡ giao diện
    }
  };
  im.onerror = () => classArtCache.set(classId, false);
  im.src = classPartsUrl(classId);
}

function classHeadHtml(classId: ClassId, size: number, cls = 'head-img', onReady?: () => void): string {
  const headUrl = classHeadDataUrl.get(classId);
  if (!headUrl) {
    if (onReady) probeClassArt(classId, onReady);
    /*
     * CHƯA CÓ ẢNH HEAD (2 class mới Thợ mộc · Thợ lặn chưa có art) ⇒ hiện **MẢNH GHÉP** có mũi tên/
     * dấu hỏi bên trong thay vì chữ Hán.
     *
     * Người dùng: *"Thay các mảnh, item trong shop bằng (VD: mảnh class sẽ có hình mảnh ghép, bên
     * trong có `head` của class đó bên trong)"* — nghĩa là hình dạng phải là MẢNH GHÉP. Khi chưa có
     * head thì giữ đúng hình mảnh ghép và để trống ruột, KHÔNG dùng ký tự Hán (chính thứ người dùng
     * yêu cầu bỏ).
     */
    return `<span class="${cls} ${cls}-empty" style="width:${size}px;height:${size}px" title="${className(classId)}"></span>`;
  }
  return `<span class="${cls} head-img-ready" style="width:${size}px;height:${size}px;background-image:url('${headUrl}')"></span>`;
}

function hexColor(c: number): string {
  return `#${(c & 0xffffff).toString(16).padStart(6, '0')}`;
}







/**
 * Class này có ảnh bộ phận chưa — dò MỘT LẦN rồi nhớ.
 *
 * ⚠️ Bẫy đã dính ở `map-art.ts`: KHÔNG được chỉ kiểm `res.ok`, vì Vite trả **200 kèm HTML** cho
 * đường dẫn tĩnh không tồn tại (fallback SPA) ⇒ ảnh chưa có vẫn tưởng là có.
 */
void classArtReady;
void classPartsUrl;

/**
 * Thoát ký tự HTML cho TÊN FILE người dùng đặt.
 * Vì sao cần: tên skill/ tên file do người dùng tự đặt trong `art/skills/` — một file tên
 * `<b>x</b>.png` sẽ phá giao diện panel nếu nhét thẳng vào HTML.
 */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

export class Hud {
  private uiGold = el('uiGold');
  /** "vé" vẽ hình xem trước — bỏ kết quả `await` cũ khi người dùng bấm đổi dáng liên tiếp */
  private gearPreviewToken = 0;
  private uiWave = el('uiWave');
  private uiCore = el('uiCore');
  private tip = el('tip');
  private btnStart = el<HTMLButtonElement>('btnStart');
  private btnSpeed = el<HTMLButtonElement>('btnSpeed');
  private btnPause = el<HTMLButtonElement>('btnPause');
  private btnAutoWave = el<HTMLButtonElement>('btnAutoWave');
  private uiDiamonds: HTMLElement | null = null;
  private uiMapText: HTMLElement | null = null;
  private btnAutoSkill = el<HTMLButtonElement>('btnAutoSkill');
  private heroList = el('heroList');
  private potionRow = el('potionRow');
  private qrChips = el('qrChips');
  private qrHeroCount = el('qrHeroCount');
  private tabs = el('tabs');
  private tabBody = el('tabBody');
  private banner = el('banner');
  private toastEl = el('toast');
  private wavebar = el('wavebar');
  private modal = el('modal');
  private modalTitle = el('modalTitle');
  private modalBody = el('modalBody');

  /**
   * HUD ARENA — MÀN HÌNH THỨ BA (chế độ BỔ SUNG).
   *
   * ⚠️ VÌ SAO LÀ ĐỐI TƯỢNG RIÊNG, KHÔNG NHÉT VÀO CLASS NÀY: `Hud` đã 3100 dòng và là HUD của trận
   * 10-wave đang chạy production. Arena khác luật (3 land · 2 Nhà · tài nguyên riêng · không wave),
   * nên HUD của nó nằm ở `src/ui/hud-arena.ts`; chỗ này chỉ giữ THAM CHIẾU để đổi màn hình và để
   * `Game` gọi `render()` theo nhịp. Người dùng đã nhấn mạnh Arena là BỔ SUNG — tách file là cách
   * duy nhất giữ việc sửa Arena không chạm vào luồng 10-wave.
   *
   * ⚠️ `null` khi `index.html` thiếu `#arenaHud`: HUD của chế độ phụ KHÔNG được phép làm sập game
   * TD lúc boot (nếu ném ở đây thì cả trận 10-wave cũng không chơi được).
   */
  arenaHud: ArenaHud | null = null;

  /**
   * ẢNH CHỤP TRẠNG THÁI ARENA GẦN NHẤT — `Game` ghi vào mỗi nhịp, HUD Arena đọc khi vẽ.
   *
   * ⚠️ VÌ SAO ẢNH CHỤP ĐI QUA `Hud` CHỨ KHÔNG ĐỂ `ArenaHud` TỰ ĐỌC `ArenaWorld`: `world` là
   * state SỐNG của mô phỏng 30Hz. Để tầng UI cầm nó là mở đường cho UI ghi vào sim — đúng thứ
   * kiến trúc repo đang chặn. `ArenaSnapshot` là DỮ LIỆU CHẾT (xem chú thích ở `hud-arena.ts`).
   *
   * ⚠️ Và vì sao KHÔNG để ở `Game`: đây là NHỊP GIAO DIỆN, không phải tiến trình — nhét vào
   * `meta` là đụng `SAVE_VERSION` (bài học đã ghi ở chú thích `showClasses()`), còn nhét vào
   * `Game` là thêm một trường nữa cho game TD phải gánh.
   */
  arenaHudSnapshot: ArenaSnapshot | null = null;

  private btnSoundEl: HTMLElement | null = null;
  /** true khi `tabBody` đang được dùng để đổ nội dung cho panel ở TRANG CHỦ */
  private homePanelActive = false;
  /**
   * HÀNH ĐỘNG khi người chơi bấm "Thoát" ở hộp thoại xác nhận — `null` = mặc định của VƯỢT ẢI
   * (bỏ trận rồi về trang chủ). Arena truyền vào `exitArena` (xem `openQuitConfirm`).
   */
  private quitAction: (() => void) | null = null;
  /** màn hình đang hiện — nguồn sự thật cho nhiều nhánh (nhịp cập nhật, ẩn/ hiện) */
  private screen: 'home' | 'game' | 'arena' = 'home';

  /** Đang ở trang chủ? (dùng để quyết định ngủ/ thức vòng lặp Phaser) */
  get isHome(): boolean {
    return this.screen === 'home';
  }

  /**
   * Đang ở MÀN HÌNH ARENA? (chế độ BỔ SUNG — màn thứ ba, xem `showScreen`).
   *
   * ⚠️ VÌ SAO CẦN GETTER RIÊNG thay vì so `screen === 'arena'` rải rác: `Game.inRun()` và nhịp
   * `renderLight()` phải biết "có đang chơi thật không" để quyết định ví nào/ có vẽ HUD 10-wave
   * không. Arena KHÔNG phải `game` (khác HUD, khác ví) nhưng CŨNG KHÔNG phải `home` (vòng lặp
   * Phaser phải thức). Một chỗ khai nghĩa, mọi chỗ khác đọc.
   */
  get isArena(): boolean {
    return this.screen === 'arena';
  }

  /**
   * Đang ở MÀN HÌNH TRONG TRẬN?
   * Dùng cho `Game.walletGold()`/`walletDiamonds()`: chỉ khi đang THẬT SỰ chơi mới hiển thị và
   * tiêu ví TRẬN. Nếu chỉ xét `world.status`, thì lúc người chơi đã về trang chủ (world vẫn còn
   * ở trạng thái `prep`) ví trận vẫn bị coi là "đang dùng" ⇒ trang chủ hiện số của ví trận.
   */
  get onGameScreen(): boolean {
    return this.screen === 'game';
  }
  /*
   * Các ô của thanh trạng thái — giữ tham chiếu để CẬP NHẬT CHỮ thay vì dựng lại HTML
   * mỗi nhịp 8 lần/giây (xem chú thích ở `renderLight`).
   */
  private wbWave: HTMLElement | null = null;
  private wbFoes: HTMLElement | null = null;
  private wbCore: HTMLElement | null = null;
  private wbState: HTMLElement | null = null;
  /** giữ tham chiếu ảnh ĐANG nạp trước (tránh GC cắt ngang); tải xong thì tự bỏ ra */
  private mapArtWarm: HTMLImageElement[] = [];
  /** URL ảnh màn đã nạp sẵn — chặn nạp lại nhiều lần (xem chú thích ở `renderHome`) */
  private mapArtWarmed = new Set<string>();
  /*
   * BẢNG GIỚI THIỆU CLASS (`?` cạnh "Chiêu mộ nhanh") — class đang xem + tab đang mở.
   *
   * ⚠️ Đây là trạng thái THUẦN GIAO DIỆN nên để Ở ĐÂY, KHÔNG nhét vào `Game.meta`:
   * `meta` là thứ được ghi vào SAVE (`localStorage`) ⇒ nhét vào là phải đụng `SAVE_VERSION` và
   * save cũ của người chơi sẽ hỏng. Đóng bảng rồi mở lại vẫn nhớ lựa chọn là đủ dùng.
   */
  private ciClass: ClassId | null = null;
  private ciTab: 'stats' | 'skills' = 'stats';
  /** đã dò ảnh AI của các màn chưa (chỉ dò 1 lần cho cả phiên) */
  private mapArtProbed = false;
  /** khi mở panel ở trang chủ: đích ghi HTML thay cho `#tabBody` */
  private panelBodyOverride: HTMLElement | null = null;
  private cards = new Map<string, HeroCardRefs>();
  private potionEls = new Map<PotionKind, HTMLButtonElement>();
  private toastTimer: number | null = null;
  private speedIndex = 0;
  private readonly speeds = [1, 2, 3];

  constructor(private game: Game) {
    const sideEl = el('side');
    this.setMobileView('battle');

    /*
     * ⚠️ ĐÃ BỎ CƠ CHẾ CHUYỂN TAB "Tác chiến / Nâng cấp & Shop" trên mobile.
     * Người dùng: *"Bỏ tab `Nâng cấp & shop`… chỉ còn lại tab Tác chiến --> không cần thiết nên
     * không cần phân tab nữa"*. Bàn điều khiển chiến đấu LUÔN hiện, không còn `data-mobile-view`.
     */

    // Nút âm thanh — nằm trong hàng điều khiển (.controls) chứ KHÔNG nằm trong
    // .side-header, vì side-header bị ẩn khi xoay ngang ⇒ nút sẽ không bấm được.
    this.btnSoundEl = document.getElementById('btnSound');
    if (this.btnSoundEl) {
      this.btnSoundEl.textContent = sound.isEnabled ? '🔊' : '🔇';
      this.btnSoundEl.addEventListener('click', () => this.toggleSound());
    }

    /*
     * NÚT CÀI ĐẶT Ở TRANG CHỦ (`#btnSettingsHome`).
     *
     * Người dùng: *"màn hình trang chủ chưa có nút cài đặt, move đổi ngôn ngữ vào trong cài đặt và
     * cài đặt phải có chức năng đổi ngôn ngữ, tắt bật tiếng"*.
     *
     * ⚠️ TRƯỚC ĐÂY trang chủ có nút ngôn ngữ RỜI `#btnLangHome`: nó chỉ `toggleLang()` (đảo VI/EN)
     * nên KHÔNG tắt/bật được tiếng, và trang chủ hoàn toàn KHÔNG có nút Cài đặt. Nay bỏ nút rời,
     * dùng chung `#settingsPanel` với trong trận ⇒ ngôn ngữ + âm thanh chỉ có MỘT nguồn sự thật
     * (`setLang` và `sound.isEnabled`), không còn 2 đường vào lệch nhau.
     *
     * Không dùng chung `#btnSettings` được: nút đó nằm trong `#topHud` chỉ hiện khi ĐANG TRONG TRẬN,
     * còn đây là nút của `#home`. Hai nút khác id nhưng gọi CÙNG `openSettings()`.
     */
    document.getElementById('btnSettingsHome')?.addEventListener('click', () => {
      sound.playClick();
      this.openSettings();
    });
    document.getElementById('btnFullscreenHome')?.addEventListener('click', () => {
      sound.playClick();
      this.toggleFullscreen();
    });

    /*
     * HUD ARENA — dựng MỘT LẦN, dùng lại cho mọi trận.
     *
     * ⚠️ Bọc `try/catch` CÓ CHỦ Ý: `ArenaHud` ném khi `index.html` thiếu `#arenaHud`. Đó là lỗi
     * CỦA CHẾ ĐỘ PHỤ, không được phép làm sập game TD lúc boot — người chơi mất luôn trận 10-wave
     * vì một tính năng bổ sung. Nuốt lỗi + `console.warn` để lần ra khi phát triển, còn chạy thật
     * thì game cũ vẫn vào được; `openHomeFeature('arena')` sẽ báo toast thay vì ném.
     */
    try {
      this.arenaHud = new ArenaHud();
    } catch (e) {
      this.arenaHud = null;
      console.warn('Không dựng được HUD Arena — chế độ Arena sẽ không mở được', e);
    }

    /*
     * Nhãn TĨNH trong `index.html` (gắn `data-i18n`) dịch tại đây; `render()` lo phần động.
     * Nghe `onLangChange` để mọi chỗ hiển thị đổi cùng lúc, không cần tải lại trang.
     *
     * ⚠️ KHÔNG còn `updateLangButton()`: đổi ngôn ngữ nay chỉ qua bảng Cài đặt, `setLang()` bắn
     * `onLangChange` ⇒ `applyStatic()` + `render()` + `renderHome()` tự vẽ lại chữ. Không còn nhãn
     * VI/EN rời nào cần cập nhật.
     */
    applyStatic();
    onLangChange(() => {
      applyStatic();
      this.render();
      this.renderHome();
      /*
       * ⚠️ HUD ARENA PHẢI VẼ LẠI KHI ĐỔI NGÔN NGỮ — nó dựng chữ bằng JS (`t(...)`), KHÔNG dùng
       * `data-i18n` nên `applyStatic()` không chạm tới. Thiếu dòng này thì đang ở màn Arena mà đổi
       * VI→EN sẽ để nguyên chữ tiếng Việt trên nút land/ nút tướng cho tới khi có nhịp sim mới —
       * lỗi mà `tests/i18n.test.ts` KHÔNG bắt được (nó kiểm bảng dịch, không kiểm DOM).
       */
      this.arenaHud?.render(this.arenaHudSnapshot);
    });

    // Nút Bắt đầu Wave
    this.btnStart.addEventListener('click', () => {
      sound.playWaveStart();
      // (đã bỏ phân tab Tác chiến/ Quản lý — bàn điều khiển luôn hiện, không cần set chế độ)
      this.game.startWave();
    });

    // Nút Tốc độ & Tạm dừng
    this.btnSpeed.addEventListener('click', () => {
      sound.playClick();
      this.speedIndex = (this.speedIndex + 1) % this.speeds.length;
      this.game.setSpeed(this.speeds[this.speedIndex]);
    });

    this.btnPause.addEventListener('click', () => {
      sound.playClick();
      this.game.togglePause();
    });

    /*
     * HAI CÔNG TẮC AUTO — chỉ ĐỔI CỜ trong Game rồi để `renderControls()` vẽ lại
     * `aria-pressed`. Không tự set class ở đây để tránh 2 nguồn sự thật.
     */
    this.btnAutoWave.addEventListener('click', () => {
      sound.playClick();
      this.game.toggleAutoNextWave();
    });
    this.btnAutoSkill.addEventListener('click', () => {
      sound.playClick();
      this.game.toggleAutoSkill();
    });

    /*
     * ⚠️ Ô "Màn 1.1 · Dễ" (`#uiMap`) KHÔNG CÒN BẤM ĐƯỢC — ĐÃ BỎ handler `goHome()`.
     *
     * Người dùng: *"trên header bar có biểu tượng thể hiện màn (giả sử 1.1 Dễ) click vào đang bị ra
     * màn hình trang chủ, tôi muốn click vào không được có action gì"*.
     *
     * Trước đây chỗ này là `addEventListener('click', () => { sound.playClick(); this.goHome(); })`
     * ⇒ bấm nhầm giữa trận là bị đá về trang chủ. Nay `#uiMap` trong `index.html` đã đổi từ
     * `<button>` sang `<div role="img">` nên không phát sinh `click` để bắt nữa; gỡ luôn listener để
     * không còn mã chết trỏ tới hành vi người dùng đã yêu cầu bỏ.
     */

    // ---- TRANG CHỦ: 4 nút chức năng + nút vào trận + đóng panel
    document.querySelectorAll<HTMLElement>('[data-home]').forEach((b) =>
      b.addEventListener('click', () => this.openHomeFeature(b.dataset.home as never)),
    );
    document.getElementById('btnStartRun')?.addEventListener('click', () => {
      sound.playClick();
      this.startFromHome();
    });
    document.getElementById('homePanelClose')?.addEventListener('click', () => {
      sound.playClick();
      this.closeHomePanel();
    });
    /*
     * NÚT BACK — thay nút Home.
     * Người dùng: *"đừng để nút Home ở màn hình gameplay, hãy để back, khi ấn nút back mà game đã
     * start thì phải hỏi có muốn thoát không, nếu có thì clear tiến trình và quay lại trang chủ"*.
     *
     * Hai ngả:
     *   · trận CHƯA bắt đầu (`!started`) ⇒ về thẳng, không hỏi (không có gì để mất).
     *   · trận ĐÃ bắt đầu ⇒ mở hộp thoại xác nhận; chỉ về khi người chơi chọn "Thoát".
     */
    document.getElementById('btnBack')?.addEventListener('click', () => {
      sound.playClick();
      if (!this.game.hasStartedRun()) {
        this.goHome();
        return;
      }
      this.openQuitConfirm();
    });
    document.getElementById('quitNo')?.addEventListener('click', () => {
      sound.playClick();
      this.closeQuitConfirm();
    });
    document.getElementById('quitYes')?.addEventListener('click', () => {
      sound.playClick();
      /*
       * ⚠️⚠️ ĐỌC HÀNH ĐỘNG **TRƯỚC** KHI ĐÓNG HỘP THOẠI — `closeQuitConfirm()` XOÁ `quitAction`.
       * Đây là bẫy đã suýt sập: nếu đọc sau khi đóng thì luôn nhận `null` và MỌI chế độ rơi về mặc
       * định của vượt ải ⇒ bấm "Thoát" ở Arena sẽ chạy `abandonRun()` của trận 10-wave (sai đường
       * dọn dẹp, và Arena không được `exitArena()` nên scene/HUD còn nguyên).
       */
      const act = this.quitAction;
      this.closeQuitConfirm();
      if (act) {
        act();
        return;
      }
      /*
       * MẶC ĐỊNH = VƯỢT ẢI: CLEAR TIẾN TRÌNH TRẬN rồi mới về trang chủ — người dùng chốt *"nếu có thì
       * clear tiến trình và quay lại trang chủ"*. `abandonRun()` xoá world/ wave/ vàng trong trận
       * (xem `game.ts`); tiến trình TRANG CHỦ (vàng tổng, class, tập luyện) KHÔNG bị đụng.
       */
      this.game.abandonRun();
      this.goHome();
    });

    // ---- NÚT CÀI ĐẶT (gộp ngôn ngữ + âm thanh)
    document.getElementById('btnSettings')?.addEventListener('click', () => {
      sound.playClick();
      this.openSettings();
    });
    document.getElementById('settingsClose')?.addEventListener('click', () => {
      sound.playClick();
      this.closeSettings();
    });
    document.querySelectorAll<HTMLElement>('[data-set-lang]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        setLang(b.dataset.setLang === 'en' ? 'en' : 'vi');
        this.refreshSettings();
      }),
    );
    document.getElementById('setSoundToggle')?.addEventListener('click', () => {
      sound.playClick();
      sound.toggle();
      this.refreshSettings();
    });
    document.getElementById('btnFullscreen')?.addEventListener('click', () => {
      sound.playClick();
      this.toggleFullscreen();
    });
    document.getElementById('setFullscreenToggle')?.addEventListener('click', () => {
      sound.playClick();
      this.toggleFullscreen();
    });
    document.getElementById('btnOpFullscreen')?.addEventListener('click', () => {
      sound.playClick();
      this.toggleFullscreen();
      this.dismissOrientationPrompt();
    });
    document.getElementById('btnOpDismiss')?.addEventListener('click', () => {
      sound.playClick();
      this.dismissOrientationPrompt();
    });

    document.addEventListener('fullscreenchange', () => {
      this.refreshFullscreenState();
      this.game.resizeArena();
    });
    document.addEventListener('webkitfullscreenchange', () => {
      this.refreshFullscreenState();
      this.game.resizeArena();
    });
    window.addEventListener('resize', () => this.checkOrientation());
    window.addEventListener('orientationchange', () => this.checkOrientation());
    this.checkOrientation();

    // bấm nền tối của panel để đóng
    document.getElementById('homePanel')?.addEventListener('click', (ev) => {
      if (ev.target === document.getElementById('homePanel')) this.closeHomePanel();
    });

    // Bấm chọn tướng hoặc kích hoạt tuyệt kỹ
    this.heroList.addEventListener('click', (ev) => {
      const target = ev.target as HTMLElement;
      const ultBtn = target.closest('[data-ult-hero]') as HTMLElement | null;
      const skillBtn = target.closest('[data-skill]') as HTMLElement | null;
      const card = target.closest('[data-hero]') as HTMLElement | null;
      if (card) {
        this.game.selectedHeroId = card.dataset.hero ?? null;
        this.render();
      }
      /*
       * NÚT ULTIMATE — ultimate là CỦA TƯỚNG, không phải của "tướng đang chọn", nên đọc thẳng
       * `data-ult-hero` của chính nút đó (giống cách `data-skill-hero` làm cho 7 ô chiêu).
       */
      if (ultBtn) {
        const heroId = ultBtn.dataset.ultHero ?? '';
        this.game.selectedHeroId = heroId;
        sound.playSkill();
        /*
         * `castUltimateOf` tự lo phần báo lỗi (toast lý do khi đang hiệu lực/ còn hồi) và trả
         * `true/false`; HUD không cần kiểm lại điều kiện.
         */
        this.game.castUltimateOf(heroId);
        this.render();
      } else if (skillBtn) {
        sound.playSkill();
        const idx = Number(skillBtn.dataset.skill);
        const heroId = skillBtn.dataset.skillHero ?? '';
        this.game.selectedHeroId = heroId;
        this.game.useSkill(idx);
      }
    });

    // Bấm dùng bình dược
    this.potionRow.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest('[data-potion]') as HTMLElement | null;
      if (btn) {
        sound.playPotion();
        this.game.usePotion(btn.dataset.potion as PotionKind);
      }
    });

    // Bấm chuyển tab quản lý
    this.tabs.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest('.tab') as HTMLElement | null;
      if (!btn) return;
      sound.playClick();
      this.game.tab = (btn.dataset.tab as Game['tab']) ?? 'roster';
      this.render();
    });

    /*
     * NÚT `?` CẠNH "CHIÊU MỘ NHANH" — giới thiệu CLASS + mô tả KỸ NĂNG từng class.
     */
    document.getElementById('btnClassInfo')?.addEventListener('click', () => {
      sound.playClick();
      this.showClasses();
    });


    el('modalClose').addEventListener('click', () => {
      sound.playClick();
      this.modal.classList.add('hidden');
    });

    // Phím tắt bàn phím
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'f' || ev.key === 'F') {
        this.toggleFullscreen();
        return;
      }
      if (ev.key === 'm' || ev.key === 'M') {
        this.toggleSound();
        return;
      }
      if (ev.code === 'Space') {
        ev.preventDefault();
        sound.playClick();
        this.game.togglePause();
      }
      if (ev.key === '1') {
        sound.playPotion();
        this.game.usePotion('hp');
      }
      if (ev.key === '2') {
        sound.playPotion();
        this.game.usePotion('mana');
      }
      if (ev.key === '3') {
        sound.playPotion();
        this.game.usePotion('stamina');
      }
    });

    window.setInterval(() => this.tick(), 120);
  }

  /** Bật/tắt âm thanh — DÙNG CHUNG cho nút bấm và phím tắt M. */
  toggleSound(): void {
    const on = sound.toggle();
    if (this.btnSoundEl) this.btnSoundEl.textContent = on ? '🔊' : '🔇';
    this.toast(on ? t('Đã bật âm thanh & rung (phím M)', 'Sound & vibration on (key M)') : t('Đã tắt âm thanh (phím M)', 'Sound off (key M)'));
  }

  // ------------------------------------------------------------ nhịp cập nhật

  private tick(): void {
    /*
     * ⚠️ CHỈ CẬP NHẬT KHI ĐANG Ở TRONG TRẬN.
     *
     * Yêu cầu người dùng: bấm lung tung ở TRANG CHỦ bị treo máy rồi crash (test trên điện thoại
     * thật qua Tailscale). Đo được: nhịp 8 lần/giây vẫn chạy khi đang ở trang chủ và ghi lại
     * `wavebar.innerHTML` **25 lần trong 3 giây** — DOM bị đập liên tục trong khi người chơi
     * đang xem trang chủ, trên điện thoại là jank/ nóng máy/ GC liên tục.
     * Nay: ở trang chủ thì thoát ngay, không đụng DOM.
     */
    if (this.screen !== 'game') return;
    this.game.pullGold();
    this.renderLight();
  }

  refreshAll(): void {
    this.cards.clear();
    this.heroList.innerHTML = '';
    this.potionRow.innerHTML = '';
    this.potionEls.clear();
    this.qrChips.innerHTML = '';
    this.render();
  }

  /**
   * Cập nhật nhẹ — chạy ~8 lần/giây.
   *
   * ⚠️ CHẶN NGAY Ở ĐÂY KHI KHÔNG Ở TRONG TRẬN, không chỉ chặn ở `tick()`.
   * `render()` (gọi từ rất nhiều chỗ: chọn tướng, mua đồ, về trang chủ…) cũng gọi hàm này, và
   * nó có dòng `wavebar.classList.remove('hidden')` ⇒ ở TRANG CHỦ mà gọi `render()` là thanh
   * trạng thái HIỆN LẠI (đúng lỗi bài kiểm bắt được: "chưa được ẩn khi ở trang chủ").
   * Chặn ở đây là chặn tận gốc cho mọi đường gọi.
   */
  renderLight(): void {
    if (this.screen !== 'game') return;
    const g = this.game;
    const w = g.world;
    if (!w) return;

    /*
     * ⚠️ HIỆN VÍ TRẬN, KHÔNG phải ví tổng.
     * Người dùng: "khi vào trận sao tôi không thấy 300 vàng?" — vì chỗ này đọc `meta.gold` (ví
     * tổng) trong khi ví trận mới là 300. Xem `Game.walletGold()`.
     */
    this.uiGold.textContent = Math.floor(g.walletGold()).toLocaleString('vi-VN');
    // KIM CƯƠNG + MÀN đang chọn: người chơi cần thấy mình đang ở đâu và còn bao nhiêu để tiêu
    this.uiDiamonds ??= document.getElementById('uiDiamonds');
    if (this.uiDiamonds) this.uiDiamonds.textContent = Math.floor(g.walletDiamonds()).toLocaleString('vi-VN');
    this.uiMapText ??= document.getElementById('uiMapText');
    if (this.uiMapText) {
      this.uiMapText.textContent = `${g.currentMapDef().id} · ${g.currentDiffDef().name}`;
    }
    this.uiWave.textContent = `${w.wave}/${w.waves.length}`;
    this.uiCore.textContent = Math.max(0, Math.round(w.core.hp)).toLocaleString('vi-VN');

    // Cập nhật thanh máu trực quan của Trụ Nhà
    const coreBar = document.getElementById('uiCoreBar');
    if (coreBar && w.core.maxHp) {
      const pct = Math.max(0, Math.min(100, (w.core.hp / w.core.maxHp) * 100));
      coreBar.style.width = `${pct}%`;
      if (pct < 25) {
        coreBar.style.background = 'linear-gradient(90deg, #dc2626, #ef4444)';
      } else if (pct < 50) {
        coreBar.style.background = 'linear-gradient(90deg, #d97706, #f59e0b)';
      } else {
        coreBar.style.background = 'linear-gradient(90deg, #16a34a, #4ade80)';
      }
    }

    // Huy hiệu số tướng
    /*
     * ⚠️ ĐÃ XOÁ KHỐI `heroCountBadge` — ĐÓ LÀ CODE CHẾT.
     * `document.getElementById('heroCountBadge')` luôn trả `null` vì id này KHÔNG có trong
     * `index.html`, nên khối `if` không bao giờ chạy. Số tướng hiển thị thật nằm ở `#qrHeroCount`
     * (hàng chiêu mộ nhanh) và ở tab "Mua tướng", cả hai đều dùng `Game.fieldedHeroCount()`.
     * Giữ lại chỉ gây nhầm tưởng là có một badge nữa đang hoạt động.
     */

    this.renderHeroBars();
    this.renderPotionCounts();
    this.updateQuickRecruitLight();

    /*
     * THANH TRẠNG THÁI — TINH TẾ, KHÔNG TRANH SỰ CHÚ Ý.
     *
     * Người dùng: "Dòng trạng thái Màn - Quái - Status cần hiển thị tinh tế hơn".
     * Bản cũ in một câu dài IN HOA ("HẾT WAVE — Nghỉ ngơi & Mua sắm") rất to, giữa đỉnh sân.
     * Nay: nhãn NGẮN, phân cấp bằng màu theo TÌNH HUỐNG (an toàn/ đang đánh/ nguy), và màu đó
     * nói thay phần chữ — mắt đọc được mà không cần một khối chữ lớn.
     *
     * ⚠️ Ghi bằng `textContent` cho từng ô thay vì `innerHTML` cả câu: nhịp cập nhật 8 lần/giây
     * (trên điện thoại là 8 lần/giây) mà `innerHTML` bắt trình duyệt PHÂN TÍCH LẠI HTML và tạo
     * lại node mỗi lần. Đổi nội dung ô có sẵn thì chỉ là cập nhật text ⇒ nhẹ hơn hẳn.
     */
    const enraged = w.isEnraged;
    const corePct = w.core.maxHp ? w.core.hp / w.core.maxHp : 1;
    let label = t('Chuẩn bị', 'Ready');
    let tone = 'safe';
    if (w.status === 'fighting') {
      if (enraged) {
        label = 'Cuồng nộ';
        tone = 'danger';
      } else if (corePct < 0.35) {
        label = 'Nguy';
        tone = 'danger';
      } else {
        label = 'Giao chiến';
        tone = 'busy';
      }
    } else if (w.status === 'waveClear') {
      label = t('Nghỉ', 'Break');
      tone = 'safe';
    } else if (w.status === 'won') {
      label = t('Qua màn', 'Cleared');
      tone = 'safe';
    } else if (w.status === 'lost') {
      label = t('Thủng nhà', 'House breached');
      tone = 'danger';
    }

    if (!this.wbWave) {
      // dựng một lần rồi chỉ cập nhật chữ (xem chú thích ở trên)
      this.wavebar.innerHTML =
        '<span>WAVE <b class="wb-wave"></b></span>' +
        '<span class="wb-foes"></span>' +
        `<span>${t('NHÀ', 'HOUSE')} <b class="wb-core"></b></span>` +
        '<span class="wb-state"></span>';
      this.wbWave = this.wavebar.querySelector<HTMLElement>('.wb-wave');
      this.wbFoes = this.wavebar.querySelector<HTMLElement>('.wb-foes');
      this.wbCore = this.wavebar.querySelector<HTMLElement>('.wb-core');
      this.wbState = this.wavebar.querySelector<HTMLElement>('.wb-state');
    }
    const waveText = `${w.wave}/${w.waves.length}`;
    if (this.wbWave && this.wbWave.textContent !== waveText) this.wbWave.textContent = waveText;
    const foesText = t('{n} quái', '{n} enemies', { n: w.enemies.length });
    if (this.wbFoes && this.wbFoes.textContent !== foesText) this.wbFoes.textContent = foesText;
    const coreText = `${Math.max(0, Math.round(corePct * 100))}%`;
    if (this.wbCore && this.wbCore.textContent !== coreText) this.wbCore.textContent = coreText;
    if (this.wbState) {
      if (this.wbState.textContent !== label) this.wbState.textContent = label;
      const cls = `wb-state ${tone}`;
      if (this.wbState.className !== cls) this.wbState.className = cls;
    }
    this.wavebar.classList.remove('hidden');
  }

  /** Cập nhật đầy đủ — khi có thay đổi trạng thái */

  // ------------------------------------------------------------ BẢNG CÀI ĐẶT + XÁC NHẬN THOÁT

  /**
   * MỞ BẢNG CÀI ĐẶT (ngôn ngữ + âm thanh).
   * Người dùng: *"Bỏ nút đổi ngôn ngữ và bật tắt âm thanh thay vào đó là nút setting"*.
   */
  openSettings(): void {
    this.refreshSettings();
    document.getElementById('settingsPanel')?.classList.remove('hidden');
  }

  closeSettings(): void {
    document.getElementById('settingsPanel')?.classList.add('hidden');
  }

  /** Đồng bộ trạng thái hiển thị của bảng cài đặt với trạng thái THẬT (lang + âm thanh). */
  private refreshSettings(): void {
    const cur = getLang();
    document.querySelectorAll<HTMLElement>('[data-set-lang]').forEach((b) => {
      b.classList.toggle('active', (b.dataset.setLang ?? 'vi') === cur);
    });
    const toggle = document.getElementById('setSoundToggle');
    const text = document.getElementById('setSoundText');
    // `sound.isEnabled` là NGUỒN SỰ THẬT của âm thanh; công tắc chỉ PHẢN ÁNH nó (không tự giữ cờ
    // riêng — nếu giữ 2 nơi thì nút và âm thanh thật sẽ lệch nhau).
    const on = sound.isEnabled;
    toggle?.setAttribute('aria-checked', on ? 'true' : 'false');
    if (text) text.textContent = on ? t('Đang bật', 'On') : t('Đang tắt', 'Off');
    this.refreshFullscreenState();
  }

  /** Bật/tắt toàn màn hình — hỗ trợ Web, Android WebView và phím tắt F */
  async toggleFullscreen(): Promise<void> {
    try {
      const doc = document as any;
      if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
        const el = document.documentElement as any;
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        }
        if (screen.orientation && 'lock' in screen.orientation) {
          (screen.orientation as any).lock('landscape').catch(() => {});
        }
      } else {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        }
      }
    } catch (e) {
      console.warn('Fullscreen error:', e);
    }
    this.refreshFullscreenState();
  }

  /** Đồng bộ icon và trạng thái của nút Toàn Màn Hình */
  refreshFullscreenState(): void {
    const doc = document as any;
    const isFs = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
    const btns = [
      document.getElementById('btnFullscreen'),
      document.getElementById('btnFullscreenHome'),
    ];
    btns.forEach((b) => {
      if (b) {
        b.innerHTML = isFs
          ? `<svg class="svg-ic" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14h6m0 0v6m0-6L3 21m17-7h-6m0 0v6m0-6l7 7M4 10h6m0 0V4m0 6L3 3m17 7h-6m0 0V4m0 6l7-7"/></svg>`
          : `<svg class="svg-ic" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>`;
        b.title = isFs ? t('Thoát toàn màn hình', 'Exit Fullscreen') : t('Toàn màn hình', 'Fullscreen');
      }
    });
    const toggle = document.getElementById('setFullscreenToggle');
    const text = document.getElementById('setFullscreenText');
    toggle?.setAttribute('aria-checked', isFs ? 'true' : 'false');
    if (text) text.textContent = isFs ? t('Đang bật', 'On') : t('Đang tắt', 'Off');
  }

  private orientationPromptDismissed = false;

  /** Kiểm tra hướng màn hình thiết bị di động để nhắc xoay ngang */
  checkOrientation(): void {
    if (this.orientationPromptDismissed) return;
    const prompt = document.getElementById('orientationPrompt');
    if (!prompt) return;
    const isPortrait = window.innerHeight > window.innerWidth && window.innerWidth <= 768;
    if (isPortrait) {
      prompt.classList.remove('hidden');
    } else {
      prompt.classList.add('hidden');
    }
  }

  dismissOrientationPrompt(): void {
    this.orientationPromptDismissed = true;
    document.getElementById('orientationPrompt')?.classList.add('hidden');
  }

  /**
   * MỞ HỘP THOẠI "THOÁT TRẬN?" — dùng CHUNG cho vượt ải và Arena.
   *
   * ⚠️⚠️ `onQuit` LÀ ĐIỂM KHÁC NHAU DUY NHẤT GIỮA HAI CHẾ ĐỘ — ĐỪNG DỰNG HỘP THOẠI THỨ HAI.
   * Người dùng yêu cầu Arena hỏi xác nhận *"giống vượt ải"* (xem `hud.onExit` ở `game.ts`). Cách rẻ
   * nhất để hai chế độ hỏi GIỐNG nhau là dùng chung đúng một hộp thoại `#quitConfirm`; dựng bản thứ
   * hai thì chúng sẽ lệch nhau ngay ở lần sửa chữ/ nút đầu tiên.
   *
   * ⚠️ MẶC ĐỊNH (`onQuit` bỏ trống) = hành vi CŨ của vượt ải: bỏ trận rồi về trang chủ. Vì vậy mọi
   * chỗ gọi cũ KHÔNG phải sửa, và `#btnBack` của vượt ải giữ nguyên hành vi.
   *
   * ⚠️ Hành động được LƯU LẠI chứ không truyền vào closure của nút: nút `#quitYes` được gắn listener
   * MỘT LẦN lúc khởi tạo (xem `bind`), nên nó không thể biết ai vừa mở hộp thoại.
   */
  openQuitConfirm(onQuit?: () => void): void {
    this.quitAction = onQuit ?? null;
    document.getElementById('quitConfirm')?.classList.remove('hidden');
  }

  closeQuitConfirm(): void {
    document.getElementById('quitConfirm')?.classList.add('hidden');
    // Đóng mà không thoát ⇒ XOÁ hành động đang chờ, nếu không lần mở sau sẽ chạy nhầm hành động cũ.
    this.quitAction = null;
  }

  render(): void {
    this.renderLight();
    this.renderControls();
    this.renderHeroCards();
    this.renderPotionButtons();
    this.renderQuickRecruit();
    this.renderTabs();
    this.renderTabBody();
  }

  // ------------------------------------------------------------ điều khiển

  private renderControls(): void {
    const g = this.game;
    const w = g.world;
    if (!w) return;
    /*
     * NÚT "BẮT ĐẦU WAVE" BỊ KHOÁ KHI:
     *   · người chơi CHƯA bấm "Vào trận" ở banner mở đầu (yêu cầu người dùng), hoặc
     *   · đang đánh / đã thua / đã hết wave.
     *
     * ⚠️ LỖI ĐÃ SỬA: nút từng KẸT ở trạng thái "Đang đánh…" sau khi hết wave, vì
     * `onStatusChange` chỉ xử lý `won`/`lost` nên HUD không được vẽ lại lúc `waveClear`.
     * Nay mọi đổi trạng thái đều vẽ lại HUD (xem `Game.onStatusChange`).
     */
    const countdown = g.startCountdown;
    const canStart = g.started && w.canStartWave();
    this.btnStart.disabled = !canStart;
    this.btnStart.title = !g.started
      ? t('Bấm "Vào trận" ở bảng giữa màn hình trước đã', 'Press "Deploy" in the centre panel first')
      : canStart
        ? t('Vào wave kế tiếp', 'Start next wave')
        : w.classHeroes.length === 0
          ? t('Cần chiêu mộ ít nhất 1 tướng để bắt đầu wave', 'Must recruit at least 1 hero to start wave')
          : '';
    this.btnStart.textContent =
      countdown !== null
        ? t('Tự vào wave {n} sau {cd}s…', 'Auto-starting wave {n} in {cd}s…', { n: w.wave + 1, cd: countdown })
        : !g.started
          ? // chưa bấm "Vào trận": nói rõ VÌ SAO nút bị khoá, đừng để "Đang đánh…" khó hiểu
            t('Chưa vào trận', 'Not deployed')
          : canStart
            ? w.wave === 0
              ? t('Bắt đầu Wave 1', 'Start Wave 1')
              : t('Wave tiếp theo ({n})', 'Next wave ({n})', { n: w.wave + 1 })
            : w.classHeroes.length === 0
              ? t('Mua tướng trước', 'Recruit hero first')
              : w.status === 'won'
                ? t('Đã qua màn', 'Stage cleared')
                : w.status === 'lost'
                  ? t('Đã thua', 'Defeated')
                  : t('Đang đánh…', 'Fighting…');
    // CÔNG TẮC AUTO: chỉ đổi `aria-pressed`, CSS lo phần nhìn ⇒ không bao giờ lệch trạng thái
    this.btnAutoWave.setAttribute('aria-pressed', String(g.autoNextWave));
    this.btnAutoSkill.setAttribute('aria-pressed', String(g.autoSkill));
    this.btnAutoSkill.title = g.autoSkill
      ? t('Đang TỰ dùng tuyệt kỹ khi có quái trong tầm chiêu — bấm để tắt', 'Auto-casting ultimates in range — tap to turn off')
      : t('Đang TẮT tự dùng tuyệt kỹ — bấm để bật', 'Auto-cast OFF — tap to turn on');
    this.btnSpeed.textContent = `×${this.speeds[this.speedIndex]}`;
    this.btnPause.textContent = g.scene?.paused ? '▶' : '❚❚';
    this.tip.innerHTML =
      countdown !== null
        ? t(
            'Hết wave {w}. <b>Tự vào wave {next} sau {cd}s</b> — bấm nút để vào ngay, hoặc tắt <b>⏭ AUTO</b>.',
            'Wave {w} over. <b>Auto-starting wave {next} in {cd}s</b> — press the button to go now, or turn off <b>⏭ AUTO</b>.',
            { w: w.wave, next: w.wave + 1, cd: countdown },
          )
        : w.status === 'fighting'
          ? `<b>${t('Wave {n}', 'Wave {n}', { n: w.wave })}</b> — ${waveTip(w.wave)}`
          : !g.started
            ? t('Bấm <b>Vào trận</b> ở bảng giữa màn hình để bắt đầu.', 'Press <b>Deploy</b> in the centre panel to begin.')
            : w.wave === 0
              ? t(
                  'Chọn tướng, rồi <b>chạm/bấm vào sân đấu</b> để ra lệnh di chuyển. Tướng tự đánh đòn võ.',
                  'Pick a hero, then <b>tap/click the arena</b> to give a move order. Heroes attack on their own.',
                )
              : t(
                  'Hết wave {w}. Vào <b>Mua tướng</b> / <b>Trang phục</b> để gia tăng sức mạnh.',
                  'Wave {w} over. Open <b>Recruit</b> / <b>Wardrobe</b> to grow stronger.',
                  { w: w.wave },
                );
  }

  // ------------------------------------------------------------ thẻ tướng

  /**
   * NẠP ẢNH HEAD CHO MỌI CLASS ĐANG Ở SÂN rồi vẽ lại thẻ tướng ĐÚNG MỘT LẦN.
   *
   * ⚠️ VÌ SAO CẦN RIÊNG HÀM NÀY: panel trang chủ có `warmClassHeads()`, nhưng TRẬN ĐẤU không đi
   * qua đó ⇒ vào trận sớm (chưa từng mở Túi đồ) thì thẻ tướng hiện mảnh ghép rỗng mãi (đo được
   * `background-image: ""` cho cả 2 thẻ). Gọi hàm này ở `attachWorld` sau khi dựng thẻ.
   */
  /**
   * Nạp ảnh head cho DẢI "CHIÊU MỘ NHANH" rồi vẽ lại MỘT LẦN khi xong.
   * Cùng lý do với `warmHeroHeads()`: dò ảnh bên trong renderer bị lần dựng sau ghi đè.
   */
  private warmQuickRecruitHeads(): void {
    let queued = false;
    const redraw = (): void => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        this.renderQuickRecruit();
      }, 0);
    };
    for (const cls of CLASS_ORDER) {
      if (classArtCache.get(cls) === false) continue;
      if (classArtReady(cls)) continue;
      probeClassArt(cls, redraw);
    }
  }

  /** Nạp ảnh head cho thẻ tướng (public để `Game` gọi sau khi dựng lại sân). */
  warmHeroHeads(): void {
    this.warmHeroCardHeads();
  }

  private warmHeroCardHeads(): void {
    let queued = false;
    const redraw = (): void => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        this.renderHeroCards();
      }, 0);
    };
    for (const cls of Object.keys(CLASSES) as ClassId[]) {
      if (classArtCache.get(cls) === false) continue; // không có ảnh (2 class mới) ⇒ bỏ qua
      if (classArtReady(cls)) continue;
      probeClassArt(cls, redraw);
    }
  }

  private renderHeroCards(): void {
    const g = this.game;
    const w = g.world;
    if (!w) return;
    /*
     * ⚠️⚠️ CHỈ HIỆN TƯỚNG ĐANG Ở TRÊN SÂN — KHÔNG hiện cả sổ tướng vĩnh viễn.
     *
     * Người dùng: "Vì sao ingame khi bắt đầu vào trận mục bên dưới dùng máu, mana, stamina lại có
     * danh sách nhân vật trong khi tôi chưa mua tướng?"
     *
     * Lỗi cũ: `g.meta.heroes.slice(...)` = SỔ TƯỚNG VĨNH VIỄN ở trang chủ ⇒ vào trận SÂN TRỐNG
     * mà danh sách vẫn đầy thẻ tướng (kèm cả nút chiêu/ bình thuốc), người chơi tưởng mình đã có
     * tướng. Nay lấy đúng ĐỘI HÌNH ĐANG Ở TRÊN SÂN (`world.fieldedLineup`), thứ tự theo đội hình.
     */
    /*
     * v7: KHONG con "so tuong" => danh sach the lay TRUC TIEP tu don vi dang o san.
     * Moi don vi hero co `kind` = classId; thu tu trong san chinh la thu tu the.
     */
    const shown = g.fieldedHeroUnits().slice(0, MAX_HEROES_ON_FIELD);

    /*
     * DỌN thẻ của tướng KHÔNG còn trên sân (ví dụ vừa vào trận mới, hoặc tướng đã bị bỏ).
     * Không dọn thì thẻ cũ nằm lại trong DOM và vẫn hiện — đúng triệu chứng người dùng thấy.
     */
    for (const [heroId, refs] of this.cards) {
      if (shown.some((h) => h.heroId === heroId)) continue;
      refs.root.remove();
      this.cards.delete(heroId);
    }

    for (const hero of shown) {
      let refs = this.cards.get(hero.heroId);
      if (!refs) {
        const cls = CLASSES[hero.classId];
        const root = document.createElement('div');
        root.className = 'hero';
        root.dataset.hero = hero.heroId;
        const stats = classStatsFor(hero.classId, g.meta);
        // ANH HEAD thay chu Han (yeu cau nguoi dung) - xem `classHeadHtml`
        /*
         * ⚠️ LỖI ĐÃ SỬA: thẻ tướng TRONG TRẬN hiện mảnh ghép rỗng vì `probeClassArt` chỉ được gọi
         * từ Túi đồ/ Shop (trang chủ). Vào trận mà chưa mở Túi đồ lần nào thì `classHeadDataUrl`
         * rỗng ⇒ mọi ô head là `head-img-empty` (đo được: 2/2 thẻ có `background-image: ""`).
         * Nay chính thẻ tướng cũng kích hoạt dò ảnh, và vẽ lại MỘT LẦN khi ảnh tải xong.
         */
        const seal = classHeadHtml(hero.classId, 34, 'head-img', () => {
          this.warmHeroCardHeads();
        });
        /*
         * ⚠️⚠️ CHỈ VẼ SỐ Ô BẰNG SỐ CHIÊU ĐÃ TRANG BỊ.
         *
         * Người dùng: *"Màn hình gameplay sao luôn luôn là 7 skill thế? Chỉ skill nào trang bị mới
         * xuất hiện thôi chứ?"*.
         *
         * LỖI CŨ: chỗ này lặp `cls.skills` = **7 chiêu GỐC của class** (danh mục), nên dù trần đã
         * hạ về 3, thanh trong trận vẫn luôn hiện 7 ô — và 4 ô thừa còn hiện cả chiêu CHƯA TRANG BỊ.
         * Nay lấy `equippedSkills(meta, classId)`: đúng số ô, đúng thứ tự ô người chơi đã xếp.
         */
        const equipped = this.game.equippedSkillsOf(hero.classId);
        const skills = equipped
          .map(
            (s, i) =>
              `<button class="skill" data-skill="${i}" data-skill-hero="${hero.heroId}" title="${skillDesc(s)} — ${t('{n} mana, hồi {cd}s (mở ở wave {w})', '{n} mana, {cd}s cooldown (unlocks at wave {w})', { n: s.manaCost, cd: s.cooldown, w: s.unlockWave })}"><span class="sk-mana">${s.manaCost} MP</span><span class="sk-name">${skillName(s)}</span><span class="cd"></span></button>`,
          )
          .join('');
        /*
         * NÚT ULTIMATE — dưới dãy 7 ô chiêu, tái dùng nguyên class CSS `.skill`/`.cd` để có sẵn
         * vòng hồi quét từ dưới lên; phần riêng của nút nằm ở `.ult`/`.ult-state` trong `style.css`.
         *
         * Chỗ này chỉ truyền MÀU qua 4 biến CSS ngắn (`--ult-*`) chứ KHÔNG viết inline style dài:
         * màu là thứ duy nhất đổi theo class, còn bố cục/ cỡ chữ/ viền thuộc về `style.css`.
         * Góc phải hiện "3:00" (3 phút) — ultimate KHÔNG tốn mana và hồi theo phút, nên mm:ss dễ
         * đọc hơn "180s".
         */
        const ult = ULTIMATES[hero.classId];
        const ultHtml = ult
          ? `<button class="skill ult" data-ult-hero="${hero.heroId}" style="--ult-color:${hexColor(ult.color)};--ult-edge:${hexColor(ult.color)}88;--ult-face:${hexColor(ult.color)}55;--ult-halo:${hexColor(ult.color)}73">
              <span class="sk-mana">${Math.floor(ult.cooldown / 60)}:${String(ult.cooldown % 60).padStart(2, '0')}</span>
              <span class="sk-name">${ultName(ult)}</span>
              <span class="ult-state"></span>
              <span class="cd"></span>
            </button>`
          : '';
        root.innerHTML = `
          <div class="avatar" style="background:linear-gradient(160deg,${hexColor(cls.color)},${hexColor(cls.accent)})">${seal}</div>
          <div class="hbody">
            <div class="hname">${className(hero.classId)}<em>${classTitle(cls.id)} · ${powerScore(stats).toLocaleString('vi-VN')} LC</em></div>
            <div class="bar hp" title="Sinh lực (HP)"><i></i></div>
            <div class="bar mana" title="Nội lực (Mana)"><i></i></div>
            <div class="bar stam" title="Thể lực"><i></i></div>
            <div class="status"></div>
            ${cls.usesAmmo ? `<div class="ammo">${t('Đạn dược:', 'Ammo:')} <b>0</b></div>` : ''}
            <div class="skills">${skills}</div>
            ${ultHtml}
          </div>`;
        this.heroList.appendChild(root);
        const bars = root.querySelectorAll<HTMLElement>('.bar > i');
        const ultBtn = root.querySelector<HTMLButtonElement>('.ult');
        refs = {
          root,
          hp: bars[0],
          mana: bars[1],
          stam: bars[2],
          skills: Array.from(root.querySelectorAll<HTMLButtonElement>('.skill:not(.ult)')),
          skillNames: Array.from(root.querySelectorAll<HTMLElement>('.skill:not(.ult) .sk-name')),
          ult: ultBtn
            ? {
                btn: ultBtn,
                name: ultBtn.querySelector<HTMLElement>('.sk-name')!,
                state: ultBtn.querySelector<HTMLElement>('.ult-state')!,
                cd: ultBtn.querySelector<HTMLElement>('.cd')!,
              }
            : undefined,
          ammo: root.querySelector<HTMLElement>('.ammo b') ?? undefined,
          status: root.querySelector<HTMLElement>('.status')!,
        };
        this.cards.set(hero.heroId, refs);
      }
    }

    // Bỏ thẻ của tướng không còn trên sân (v7: nguồn sự thật là ĐƠN VỊ, không phải sổ)
    const alive = new Set(g.fieldedHeroUnits().map((h) => h.heroId));
    for (const [id, refs] of this.cards) {
      if (!alive.has(id)) {
        refs.root.remove();
        this.cards.delete(id);
      }
    }
    this.renderHeroBars();
  }

  private renderHeroBars(): void {
    const g = this.game;
    const w = g.world;
    if (!w) return;
    // chỉ cập nhật thẻ của tướng ĐANG Ở TRÊN SÂN (xem chú thích ở `renderHeroCards`)
    for (const hero of g.fieldedHeroUnits()) {
      const refs = this.cards.get(hero.heroId);
      if (!refs) continue;
      const unit = g.unitForHero(hero.heroId);
      const selected = g.selectedHeroId === hero.heroId;
      refs.root.classList.toggle('selected', selected);
      if (!unit) {
        refs.hp.style.width = '0%';
        refs.mana.style.width = '0%';
        refs.stam.style.width = '0%';
        refs.status.textContent = t('Chưa vào sân (tối đa 5 tướng)', 'Not deployed (max 5 heroes)');
        refs.root.classList.add('dead');
        for (const b of refs.skills) b.disabled = true;
        /*
         * Tướng CHƯA VÀO SÂN vẫn phải đồng bộ TÊN/ tooltip/ nút ULT: đổi ngôn ngữ lúc này mà bỏ qua
         * thì thẻ giữ nguyên chữ ngôn ngữ cũ cho tới khi tướng ra sân. Không truyền `unit` vì không có.
         */
        this.syncHeroCardText(hero.classId, refs, null, w);
        continue;
      }
      const hpPct = Math.max(0, Math.min(1, unit.hp / Math.max(1, unit.base.hp)));
      const manaPct = Math.max(0, Math.min(1, unit.mana / Math.max(1, unit.base.mana)));
      const stamPct = Math.max(0, Math.min(1, unit.stamina / Math.max(1, unit.base.stamina)));
      refs.hp.style.width = `${hpPct * 100}%`;
      refs.mana.style.width = `${manaPct * 100}%`;
      refs.stam.style.width = `${stamPct * 100}%`;
      refs.root.classList.toggle('dead', !unit.alive);
      refs.status.innerHTML = !unit.alive
        ? `<b style="color:#ef4444">⚔ ${t('ĐÃ GỤC', 'DOWN')}</b> — ${t('Chờ wave sau hồi sinh', 'Revives next wave')}`
        : `<b>${Math.round(unit.hp)}</b>/${Math.round(unit.base.hp)} ${t('máu', 'HP')} · <b>${Math.round(unit.mana)}</b> mana${unit.invulnUntil > w.time ? ` · <b style="color:#fbbf24">⚡ ${t('BẤT TỬ', 'INVULNERABLE')}</b>` : ''}`;
      if (refs.ammo) refs.ammo.textContent = String(unit.ammo);

      // ⚠️ theo bộ ĐÃ TRANG BỊ (không phải `cls.skills`) — xem chú thích ở chỗ dựng nút
      const equipped = this.game.equippedSkillsOf(hero.classId);
      refs.skills.forEach((btn, i) => {
        const sk = equipped[i];
        if (!sk) return;
        const cdLeft = Math.max(0, (unit.skillCds[i] ?? 0) - w.time);
        const locked = w.wave < sk.unlockWave;
        const noMana = unit.mana < sk.manaCost;
        btn.disabled = locked || noMana || cdLeft > 0 || !unit.alive;
        const cdEl = btn.querySelector<HTMLElement>('.cd');
        if (cdEl) {
          const pct = cdLeft > 0 ? cdLeft / sk.cooldown : 0;
          cdEl.style.transform = `scaleY(${pct})`;
          cdEl.style.display = pct > 0.01 ? 'block' : 'none';
        }
      });
      // TÊN + tooltip của 7 chiêu và nút ULT: 1 chỗ duy nhất (xem `syncHeroCardText`)
      this.syncHeroCardText(hero.classId, refs, unit, w);
    }
  }

  /**
   * ĐỒNG BỘ CHỮ CỦA THẺ TƯỚNG (tên 7 chiêu + tên/ trạng thái ultimate).
   *
   * ⚠️ VÌ SAO CẦN HÀM RIÊNG: `renderHeroCards()` chỉ dựng HTML ở nhánh `!refs` — thẻ đã có thì TÁI
   * DÙNG nguyên DOM. Nên mọi chữ sinh ra từ `t()`/`loc.ts` phải được ghi lại ở đây theo từng khung
   * hình (và CHỈ ghi khi giá trị thật sự đổi), nếu không thì bấm nút VI/EN xong thẻ vẫn nằm nguyên
   * ngôn ngữ cũ. Trước đây `.sk-name` bị đúng lỗi này.
   *
   * `unit = null` = tướng chưa vào sân: vẫn đổi được TÊN (không phụ thuộc đơn vị), nhưng trạng thái
   * thì để `syncUltButton` tự lo phần "không bấm được".
   */
  private syncHeroCardText(classId: ClassId, refs: HeroCardRefs, unit: Unit | null, w: World): void {
    // ⚠️ theo bộ ĐÃ TRANG BỊ — `cls.skills` là danh mục 7 chiêu gốc, không phải bộ đang mang
    const equipped = this.game.equippedSkillsOf(classId);
    refs.skills.forEach((btn, i) => {
      const sk = equipped[i];
      if (!sk) return;
      const nameEl = refs.skillNames[i];
      if (nameEl) {
        const label = skillName(sk);
        if (nameEl.textContent !== label) nameEl.textContent = label;
      }
      /*
       * TOOLTIP cũng theo ngôn ngữ: `skillDesc()` + `t()` đều đổi khi sang EN. Ghi lại ở đây thay vì
       * chỉ lúc dựng thẻ. Nội dung y hệt nhánh trong `renderHeroBars` (gồm cả lý do "chưa đủ mana"
       * khi tướng đã ra sân) — chỉ khác là KHÔNG phụ thuộc trạng thái nút nên tướng chưa vào sân vẫn
       * đọc được mô tả chiêu.
       */
      const locked = w.wave < sk.unlockWave;
      const noMana = unit !== null && unit.mana < sk.manaCost;
      const title = locked
        ? t('Mở ở wave {n}', 'Unlocks at wave {n}', { n: sk.unlockWave })
        : `${skillDesc(sk)} — ${t('{n} mana, hồi {cd}s', '{n} mana, {cd}s cooldown', { n: sk.manaCost, cd: sk.cooldown })}${noMana ? ` — ${t('Chưa đủ mana', 'Not enough mana')}` : ''}`;
      if (btn.title !== title) btn.title = title;
    });
    this.syncUltButton(classId, refs, unit, w);
  }

  /**
   * CẬP NHẬT NÚT ULTIMATE của 1 tướng — gọi từ `renderHeroBars()` (nhịp làm tươi CÓ SẴN của thẻ
   * tướng), KHÔNG tạo vòng lặp/ timer mới.
   *
   * 3 trạng thái người dùng yêu cầu:
   *   · `Sẵn sàng`          — bấm được ngay;
   *   · đang HIỆU LỰC       — đếm ngược thời gian còn lại;
   *   · đang HỒI            — đếm ngược tới lúc dùng được.
   *
   * NGUỒN SỐ LIỆU: dùng ĐÚNG 2 hàm của `sim/` là `World.ultActive()` / `World.ultCooldownLeft()`
   * thay vì tự so `ultUntil`/`ultReadyAt` với `time` — luật hồi nằm 1 chỗ duy nhất ở `sim/`, HUD
   * không giữ bản sao của luật đó (đổi luật ở `sim/` là nút tự đúng theo).
   */
  private syncUltButton(
    classId: ClassId,
    refs: HeroCardRefs,
    unit: Unit | null,
    w: World,
  ): void {
    const ult = refs.ult;
    if (!ult) return;
    const def = ULTIMATES[classId];
    /*
     * TÊN: ghi lại mỗi khung hình nhưng CHỈ khi thật sự đổi (so sánh trước khi gán) — nhờ vậy đổi
     * ngôn ngữ là tên tự sang tiếng Anh mà không tốn thao tác DOM mỗi frame.
     */
    const name = ultName(def);
    if (ult.name.textContent !== name) ult.name.textContent = name;

    /*
     * TƯỚNG CHƯA VÀO SÂN (`unit = null`): vẫn phải hiện TÊN + chữ trạng thái đúng ngôn ngữ, nhưng
     * khoá nút (không có đơn vị thì không niệm được).
     */
    const active = unit ? w.ultActive(unit) : false;
    const cdLeft = unit ? w.ultCooldownLeft(unit) : 0;
    const usable = unit !== null && unit.alive;
    /*
     * ĐANG HIỆU LỰC: ultimate của Đẻ mướn không có đồng hồ (`ultUntil` = Infinity, xem
     * `UltimateDef.duration = 0`) ⇒ hiện "Đang hiệu lực" chứ không in "Infinity".
     */
    const activeLeft = active && unit ? unit.ultUntil - w.time : 0;

    let stateText: string;
    let stateColor: string;
    if (active) {
      stateText = Number.isFinite(activeLeft)
        ? `${t('Hiệu lực', 'Active')} ${Math.ceil(activeLeft)}s`
        : t('Hiệu lực', 'Active');
      stateColor = '#fbbf24';
    } else if (cdLeft > 0) {
      // hiện SỐ GIÂY còn hồi (yêu cầu: "đang hồi ⇒ đếm ngược tới lúc dùng được")
      stateText = `${t('Hồi', 'CD')} ${Math.ceil(cdLeft)}s`;
      stateColor = '#a1a1aa';
    } else {
      stateText = t('Sẵn sàng', 'Ready');
      stateColor = '#4ade80';
    }
    ult.state.textContent = stateText;
    // màu chữ đổi theo trạng thái ⇒ đây là 1 thuộc tính ĐỘNG, để inline cho gọn
    ult.state.style.color = stateColor;
    ult.btn.classList.toggle('active', active);
    ult.btn.classList.toggle('ready', !active && cdLeft <= 0 && usable);
    ult.btn.disabled = active || cdLeft > 0 || !usable;

    // vòng hồi: đầy dần từ dưới lên y như ô chiêu thường, nhưng chia cho `cooldown` của ultimate
    const pct = cdLeft > 0 ? cdLeft / Math.max(1, def.cooldown) : 0;
    ult.cd.style.transform = `scaleY(${pct})`;
    ult.cd.style.display = pct > 0.01 ? 'block' : 'none';

    ult.btn.title = active
      ? t(
          '{desc} — đang hiệu lực, còn {s}s',
          '{desc} - active, {s}s left',
          { desc: ultDesc(def), s: Number.isFinite(activeLeft) ? Math.ceil(activeLeft) : '∞' },
        )
      : cdLeft > 0
        ? t(
            '{desc} — đang hồi, còn {s}s',
            '{desc} - recharging, {s}s left',
            { desc: ultDesc(def), s: Math.ceil(cdLeft) },
          )
        : t('{desc} — bấm để dùng NGAY', '{desc} - click to cast NOW', { desc: ultDesc(def) });
  }

  // ------------------------------------------------------------ bình thuốc

  private renderPotionButtons(): void {
    for (const kind of ['hp', 'mana', 'stamina'] as PotionKind[]) {
      if (this.potionEls.has(kind)) continue;
      const def = POTIONS[kind];
      const btn = document.createElement('button');
      btn.className = 'potion';
      btn.dataset.potion = kind;
      btn.title = `${potionDesc(kind)} — ${t('Phím tắt {key}', 'Hotkey {key}', { key: kind === 'hp' ? 1 : kind === 'mana' ? 2 : 3 })}`;
      btn.innerHTML = `<span class="ic" style="color:${hexColor(def.color)}">${def.icon}</span><span class="n"><span class="potion-label">${potionShortName(kind)}</span> <b data-count>0</b></span>`;
      this.potionRow.appendChild(btn);
      this.potionEls.set(kind, btn);
    }
    this.renderPotionCounts();
  }

  private renderPotionCounts(): void {
    const w = this.game.world;
    if (!w) return;
    for (const [kind, btn] of this.potionEls) {
      const count = w.potions[kind];
      const b = btn.querySelector<HTMLElement>('[data-count]');
      if (b) b.textContent = String(count);
      const selectedOk = this.game.selectedUnitId() !== null;
      const staminaBlocked = kind === 'stamina' && w.staminaPotionsUsed >= 2;
      btn.disabled = count <= 0 || !selectedOk || staminaBlocked;
      btn.title = staminaBlocked
        ? t('Đã dùng tối đa 2 bình thể lực trong màn này', 'Used the max 2 stamina potions this stage')
        : POTIONS[kind].desc;
    }
  }

  // ------------------------------------------------------------ chiêu mộ nhanh

  private renderQuickRecruit(): void {
    const g = this.game;
    // đếm TƯỚNG ĐANG Ở TRÊN SÂN (còn sống), không phải sổ tướng vĩnh viễn
    const heroCount = g.fieldedHeroCount();
    const isFull = g.isFieldFull();

    this.qrHeroCount.textContent = t('{n}/{max} tướng', '{n}/{max} heroes', { n: heroCount, max: MAX_HEROES_ON_FIELD });
    this.qrHeroCount.classList.toggle('full', isFull);

    this.qrChips.innerHTML = CLASS_ORDER.map((c) => {
      const cls = CLASSES[c];
      const price = g.heroPriceOf(c);
      /*
       * ẢNH HEAD thay chữ Hán — người dùng: *"Avatar lúc nãy bạn thêm thay cho chữ trung quốc"*,
       * nghĩa là yêu cầu áp dụng ở MỌI chỗ nhận diện class, KHÔNG chỉ thẻ tướng.
       * Đây là dải "Chiêu mộ nhanh" (5 chip class) — trước vẫn hiện `力 媚 市 槍 育`.
       * `onReady` vẽ lại dải này khi ảnh tải xong (lần mở đầu tiên ảnh còn đang tải).
       */
      const seal = classHeadHtml(c, 30, 'head-img', () => this.warmQuickRecruitHeads());
      // trong trận: ví TRẬN + giá TRẬN (xem `Game.walletGold`/`heroPriceOf`)
      const canAfford = g.walletGold() >= price;
      const owned = g.fieldedHeroUnits().filter((h) => h.classId === c).length;
      const disabled = isFull || !canAfford;

      return `<button class="qr-chip ${canAfford ? 'affordable' : 'unaffordable'}${isFull ? ' full' : ''}"
        data-quick-buy="${c}"
        ${disabled ? 'disabled' : ''}
        title="${className(cls.id)} (${classRole(cls.id)}) — ${t('{n} vàng · Trong sổ: {own} nhân vật', '{n} gold · Roster: {own} heroes', { n: price.toLocaleString('vi-VN'), own: owned })}">
        <span class="qr-seal">${seal}</span>
        <span class="qr-info">
          <span class="qr-name">${className(cls.id)}</span>
          <span class="qr-price">${price.toLocaleString('vi-VN')}🪙</span>
        </span>
      </button>`;
    }).join('');

    this.qrChips.querySelectorAll<HTMLElement>('[data-quick-buy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        sound.playClick();
        const cid = btn.dataset.quickBuy as ClassId;
        g.doBuyHero(cid);
      });
    });
  }

  private updateQuickRecruitLight(): void {
    const g = this.game;
    const heroCount = g.fieldedHeroCount();
    const isFull = g.isFieldFull();
    this.qrHeroCount.textContent = t('{n}/{max} tướng', '{n}/{max} heroes', { n: heroCount, max: MAX_HEROES_ON_FIELD });
    this.qrHeroCount.classList.toggle('full', isFull);

    this.qrChips.querySelectorAll<HTMLButtonElement>('[data-quick-buy]').forEach((btn) => {
      const c = btn.dataset.quickBuy as ClassId;
      const price = g.heroPriceOf(c);
      const canAfford = g.walletGold() >= price;
      btn.disabled = isFull || !canAfford;
      btn.classList.toggle('affordable', canAfford);
      btn.classList.toggle('unaffordable', !canAfford);
      const priceEl = btn.querySelector('.qr-price');
      if (priceEl) priceEl.textContent = `${price.toLocaleString('vi-VN')}🪙`;
    });
  }

  // ------------------------------------------------------------ tab

  private renderTabs(): void {
    for (const b of Array.from(this.tabs.querySelectorAll<HTMLElement>('.tab'))) {
      b.classList.toggle('active', b.dataset.tab === this.game.tab);
    }
  }

  private renderTabBody(): void {
    const g = this.game;
    /*
     * ĐÍCH GHI: bình thường là `#tabBody` (trong game); khi mở panel ở TRANG CHỦ thì là
     * `#homePanelBody`. Nhờ vậy mọi hàm `renderXxx()` giữ nguyên, không phải viết bản thứ hai.
     */
    /*
     * ⚠️ ĐỔI TẠM RỒI PHỤC HỒI: nếu gán đè thẳng `this.tabBody = panel` thì sau đó sidebar TRONG
     * GAME sẽ ghi vào panel của trang chủ — lỗi rất khó thấy về sau. Lưu lại rồi trả về cuối hàm.
     */
    const restore = this.tabBody;
    if (this.homePanelActive && this.panelBodyOverride) this.tabBody = this.panelBodyOverride;
    switch (g.tab) {
      case 'roster': this.renderRoster(); break;
      case 'gear': this.renderGear(); break;
      case 'train': this.renderTrain(); break;
      case 'time': this.renderTimeRoom(); break;
      case 'skill': this.renderSkill(); break;
      case 'shop': this.renderShopGems(); break;
      case 'bag': this.renderBag(); break;
    }
    this.tabBody = restore;
  }

  // ------------------------------------------------------------ mua tướng

  private renderRoster(): void {
    const g = this.game;
    const chips = CLASS_ORDER.map(
      (c) =>
        `<button class="chip${g.shopClass === c ? ' active' : ''}" data-shop-class="${c}">${className(c)}</button>`,
    ).join('');
    const cls = CLASSES[g.shopClass];
    const price = g.heroPriceOf(g.shopClass);
    /*
     * ⚠️ "ĐANG CÓ" = SỐ TƯỚNG ĐANG Ở TRÊN SÂN, cùng nguồn với mua nhanh.
     *
     * Người dùng: "Mục mua tướng trong Nâng cấp và shop đang không đồng nhất với mua nhanh".
     * Nguyên nhân: tab này đếm `meta.heroes` (sổ tướng vĩnh viễn) và **KHÔNG hề kiểm tra sân đã
     * đủ 5 tướng hay chưa**, trong khi mua nhanh thì khoá nút khi đủ. Kết quả: cùng lúc, mua nhanh
     * báo "5/5" và khoá hết, mà tab này vẫn cho bấm Mua ⇒ hai nơi nói hai chuyện khác nhau.
     * Nay cả hai dùng CHUNG `fieldedHeroCount()`/`isFieldFull()` và cùng điều kiện khoá.
     */
    const owned = g.fieldedHeroCount();
    // `block`: LÝ DO bị chặn (null = mua được). Phân biệt "sân đủ 5" và "sổ đủ 5" — xem chú thích ở nút.
    const block = g.recruitBlockReason(g.shopClass);
    const cantAfford = g.walletGold() < price;
    const sample = classStatsFor(g.shopClass, g.meta);
    this.tabBody.innerHTML = `
      <div class="chips">${chips}</div>
      <div class="rowitem">
        <div>
          <div class="t">${className(cls.id)} — ${classRole(cls.id)}</div>
          <div class="d">${classBlurb(cls.id)}</div>
          <div class="d">HP ${Math.round(sample.hp)} · ATK ${Math.round(sample.atk)} · DEF ${Math.round(sample.def)} · ${t('Tốc', 'SPD')} ${sample.atkSpeed.toFixed(2)}/s · ${t('Né', 'Dodge')} ${(sample.dodge * 100).toFixed(0)}%</div>
          <div class="d">${t('Đang có', 'On field')}: <b>${owned}/${MAX_HEROES_ON_FIELD}</b> · ${t('Class đã mở khoá', 'Unlocked classes')}: <b>${g.meta.unlocked.length}</b> · ${t('Mảnh', 'Shards')}: <b>${g.meta.classShards[g.shopClass] ?? 0}</b></div>
        </div>
        <div style="text-align:right">
          <div class="p">${t('{n} vàng', '{n} gold', { n: price.toLocaleString('vi-VN') })}</div>
          <button class="btn primary" data-buy-class="${g.shopClass}" ${block ? 'disabled' : ''}>${
            /*
             * ⚠️ NÚT PHẢI NÓI ĐÚNG LÝ DO. Người dùng từng bị báo *"vượt 5 tướng"* khi thật ra SÂN
             * đã đủ 5 tướng cũ ⇒ tưởng game sai luật. 2 lý do khác hẳn nhau nên hiện 2 chữ khác:
             *   fieldFull — sân đủ 5 tướng đang sống, đợi tướng gục hoặc qua màn
             *   locked    — class CHƯA mở khoá (phải gom đủ mảnh ở Shop/ Túi đồ)
             *   poor      — chưa đủ vàng
             */
            block === 'fieldFull'
              ? t('Sân đủ {n}', 'Field full ({n})', { n: MAX_HEROES_ON_FIELD })
              : block === 'locked'
                ? t('Chưa mở khoá', 'Locked')
                : block === 'poor'
                  ? t('Thiếu vàng', 'Not enough gold')
                  : t('Mua', 'Buy')
          }</button>
        </div>
      </div>
      <div class="section-label">${t('Tuyệt kỹ của {cls}', 'Ultimates of {cls}', { cls: className(cls.id) })}</div>
      ${cls.skills
        .map(
          (s, i) =>
            `<div class="rowitem"><div><div class="t">${i + 1}. ${skillName(s)}</div><div class="d">${skillDesc(s)}</div></div><div class="p">wave ${s.unlockWave}<br/>${s.manaCost} mana</div></div>`,
        )
        .join('')}`;

    this.tabBody.querySelectorAll<HTMLElement>('[data-shop-class]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.shopClass = b.dataset.shopClass as ClassId;
        this.render();
      }),
    );
    this.tabBody.querySelectorAll<HTMLElement>('[data-buy-class]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.doBuyHero(b.dataset.buyClass as ClassId);
      }),
    );
  }

  // ------------------------------------------------------------ trang phục (dáng theo class)

  /**
   * XEM TRƯỚC NHÂN VẬT TRONG TAB TRANG PHỤC.
   *
   * Người dùng (lần trước): "Tôi muốn màn hình phụ kiện khi trang bị tôi có thể preview được nhân vật của tôi
   * trông thế nào".
   *
   * Cách làm: vẽ chính các mảnh rig (ảnh AI hoặc vector) ra một `<canvas>` nhỏ, dùng ĐÚNG phép
   * biến đổi mà tầng cảnh dùng cho sprite (`x/y/rotation/scaleX/scaleY`, xem `UnitViews.sync`).
   * Nhờ vậy hình xem trước GIỐNG HỆT hình trong trận — không phải bản vẽ lại bằng tay rồi lệch.
   *
   * ⚠️ Dựng rig MỚI theo DÁNG đang xem, không lấy rig trên sân: tab Trang phục ở TRANG CHỦ, mà
   * mỗi trận bắt đầu với sân TRỐNG nên tướng đang xem thường không có mặt trên sân.
   */
  private gearPreviewHtml(caption: string): string {
    return `<div class="gear-preview">
      <canvas id="gearPreview" width="150" height="190" aria-label="${t('Xem trước nhân vật', 'Hero preview')}"></canvas>
      <div class="d" style="font-size:11.5px;color:var(--paper-dim)">${caption}</div>
    </div>`;
  }

  /**
   * Vẽ nhân vật vào `#gearPreview` theo DÁNG đang xem. Gọi sau mỗi lần đổi class/ đổi dáng.
   *
   * ⚠️ BẤT ĐỒNG BỘ + CÓ "VÉ": dáng là ảnh người dùng thả vào, lần đầu xem phải cắt ảnh (chậm
   * vài chục ms). Người dùng bấm liên tiếp nhiều dáng thì các lần `await` về KHÔNG theo thứ tự
   * ⇒ phải bỏ kết quả cũ, nếu không hình của dáng trước sẽ vẽ đè lên dáng vừa chọn.
   */
  private async drawGearPreview(classId: ClassId, formId: string | undefined): Promise<void> {
    const canvas = document.getElementById('gearPreview') as HTMLCanvasElement | null;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scene = this.game.scene;
    if (!scene) return;
    const token = ++this.gearPreviewToken;
    let rig: Awaited<ReturnType<typeof scene.units.previewRig>>;
    try {
      rig = await scene.units.previewRig(classId, formId);
    } catch {
      return; // thiếu art ⇒ để trống, không làm hỏng panel
    }
    if (token !== this.gearPreviewToken) return; // đã có lượt vẽ mới hơn
    if (document.getElementById('gearPreview') !== canvas) return; // panel đã được vẽ lại
    const resolved = rig.update();
    if (!resolved.length) return;

    const atlas = scene.textures.get(PARTS_ATLAS);
    const src = atlas?.getSourceImage() as CanvasImageSource | undefined;
    if (!src) return;

    /*
     * KHUNG BAO của toàn bộ mảnh ⇒ canh nhân vật vào giữa canvas và chọn tỉ lệ vừa khung.
     * Tính theo `scaleX/scaleY` vì mảnh có thể bị co giãn khác nhau.
     */
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const rp of resolved) {
      const f = atlas.get(rp.part.key);
      if (!f) continue;
      const hw = (f.width * rp.scaleX) / 2;
      const hh = (f.height * rp.scaleY) / 2;
      minX = Math.min(minX, rp.x - hw);
      maxX = Math.max(maxX, rp.x + hw);
      minY = Math.min(minY, rp.y - hh);
      maxY = Math.max(maxY, rp.y + hh);
    }
    if (!Number.isFinite(minX)) return;
    const pad = 8;
    const bw = Math.max(1, maxX - minX);
    const bh = Math.max(1, maxY - minY);
    const k = Math.min((canvas.width - pad * 2) / bw, (canvas.height - pad * 2) / bh);
    const ox = canvas.width / 2 - ((minX + maxX) / 2) * k;
    const oy = canvas.height / 2 - ((minY + maxY) / 2) * k;

    /* Vẽ theo THỨ TỰ z tăng dần — trùng cách tầng cảnh xếp lớp (tay/ chân/ thân/ đầu). */
    const ordered = [...resolved].sort((a, b) => a.z - b.z);
    for (const rp of ordered) {
      const f = atlas.get(rp.part.key);
      if (!f) continue;
      ctx.save();
      ctx.translate(ox + rp.x * k, oy + rp.y * k);
      ctx.rotate(rp.rotation);
      ctx.scale(rp.scaleX * k, rp.scaleY * k);
      ctx.drawImage(src, f.cutX, f.cutY, f.width, f.height, -f.width / 2, -f.height / 2, f.width, f.height);
      ctx.restore();
    }
  }

  /**
   * TAB TRANG PHỤC — chọn CLASS rồi chọn DÁNG (skill) của class đó, kèm hình xem trước.
   *
   * Người dùng: *"Trong tab trang phục giờ không chọn phụ kiện nữa mà sẽ cho chọn theo class,
   * khi chọn class nào sẽ ra skills của class đó… khi tôi chọn skill nhân vật sẽ hiển thị các
   * bộ phận theo skill tôi chọn. Tên skill chính là tên file 3x3 tôi để vào thư mục đó."*
   *
   * ⚠️ KHÔNG cần chọn tướng nữa: dáng thuộc về CLASS (mọi tướng cùng class mặc chung), nên
   * người chơi xem được dáng của class mình CHƯA mua.
   *
   * ⚠️ ĐÃ BỎ TAB "Vào sân" (người dùng: *"Bỏ nút `Vào sân` ở phần trang phục, sắp xếp lại các nút
   * cho gọn"*).
   *
   * Vì sao bỏ được mà không mất chức năng: tab đó chỉ là danh sách tướng để CHỌN tướng đang điều
   * khiển — mà việc đó đã có sẵn ở THẺ TƯỚNG trong màn chiến đấu (`selectedHeroId`, xem
   * `renderHeroes`). Nó là bản sao thứ hai của cùng một hành động, lại nằm trong tab trang phục
   * (không liên quan gì tới quần áo) ⇒ gây rối.
   *
   * Nhờ bỏ tab đó, hàng nút nay chỉ còn 5 chip CLASS thuần nhất ⇒ sắp xếp lại cho gọn, chia đều
   * bề rộng (xem `.chips-eq` trong `style.css`).
   * ⚠️ Chia đều KHÔNG có nghĩa là ép vào một hàng: ở màn hẹp, chip xuống hàng chứ KHÔNG được cắt
   * chữ — lý do đầy đủ nằm ở comment `.chips-eq`.
   */
  private renderGear(): void {
    const g = this.game;
    const cls = g.shopClass;
    const slots = `<div class="chips chips-eq">${CLASS_ORDER.map(
      (c) => `<button class="chip${cls === c ? ' active' : ''}" data-form-class="${c}">${className(c)}</button>`,
    ).join('')}</div>`;

    /*
     * ⚠️ NHÁNH `wardrobeTab === 'battle'` ĐÃ BỊ XOÁ HẲN (cùng với tab "Vào sân"), và field
     * `wardrobeTab` trong `game.ts` cũng đã bị XOÁ theo — không còn nút nào set nó nữa nên giữ
     * lại chỉ là mã chết.
     * Chọn tướng đang điều khiển nay chỉ có MỘT đường: bấm thẻ tướng trong màn chiến đấu.
     */
    {
      /*
       * CHỌN DÁNG THEO CLASS — danh sách skill KHÔNG nằm trong code mà quét từ thư mục
       * `art/skills/<class>/` (xem `data/wardrobe.ts`): thả ảnh 3×3 vào là có thêm dòng.
       */
      const forms = formsForClass(cls);
      const chosen = g.meta.formByClass[cls];
      const chosenDef = chosen ? skillFormById(cls, chosen) : undefined;
      const chosenName = chosenDef?.name ?? chosen ?? '';
      /*
       * ⚠️ ẢNH BỊ XOÁ/ ĐỔI TÊN SAU KHI ĐÃ CHỌN: save vẫn giữ `id` cũ (không tự dọn — người dùng có
       * thể đổi tên file rồi đổi lại). Phải NÓI RÕ, nếu không người chơi chỉ thấy "chọn rồi mà
       * nhân vật không đổi" — đúng lỗi khó đoán nhất của tính năng này.
       */
      const staleHint =
        chosen && !chosenDef
          ? `<div class="rowitem"><div class="d" style="color:#fca5a5">${t('Dáng đang mặc', 'Worn look')} <b>“${esc(chosen)}”</b> ${t('không còn ảnh trong', 'has no image left in')} <code>art/skills/${cls}/</code> ${t('(file đã bị xoá hoặc đổi tên) — nhân vật đang tạm dùng dáng gốc. Chọn lại bên dưới.', '(the file was deleted or renamed) — the hero is temporarily using the base look. Pick another below.')}</div></div>`
          : '';
      const baseRow = `<div class="rowitem${chosen ? '' : ' owned'}">
        <div>
          <div class="t">${t('Dáng gốc của class', 'Class base look')}</div>
          <div class="d">${t('Ảnh bộ phận có sẵn của', 'Built-in body-part art of')} ${className(cls)} (${classRole(cls)})</div>
        </div>
        ${chosen ? `<button class="btn ghost" data-form="">${t('Mặc', 'Wear')}</button>` : `<button class="btn ghost" disabled>${t('Đang mặc', 'Worn')}</button>`}
      </div>`;
      const formRows = forms
        .map((f) => {
          const equipped = chosen === f.id;
          const issues = g.scene?.units.issuesFor(cls, f.id);
          const bad = issues?.length
            ? `<div class="d" style="color:#fca5a5">${t('Ảnh lỗi:', 'Bad images:')} ${issues.slice(0, 2).join(' · ')}</div>`
            : '';
          return `<div class="rowitem${equipped ? ' owned' : ''}">
            <div>
              <div class="t">${esc(f.name)}</div>
              <div class="d">${t('Tệp', 'File')} <code>art/skills/${cls}/${esc(f.file)}</code></div>
              ${bad}
            </div>
            ${equipped ? `<button class="btn ghost" disabled>${t('Đang mặc', 'Worn')}</button>` : `<button class="btn" data-form="${f.id}">${t('Mặc', 'Wear')}</button>`}
          </div>`;
        })
        .join('');
      const emptyHint = forms.length
        ? ''
        : `<div class="rowitem"><div class="d">${t('Thư mục', 'The folder')} <code>art/skills/${cls}/</code> ${t('chưa có ảnh 3×3 nào. Thả ảnh PNG (lưới 3×3, nền magenta) vào đó rồi', 'has no 3×3 image yet. Drop a PNG (3×3 grid, magenta background) in there, then')} <b>${t('tải lại trang', 'reload the page')}</b> — <b>${t('tên file chính là tên skill', 'the file name is the skill name')}</b>. ${t('Xem', 'See')} <code>art/skills/README.md</code> ${t('để biết bố cục 9 ô.', 'for the 9-cell layout.')}</div></div>`;
      this.tabBody.innerHTML = `<div class="chips">${slots}</div>
        <div class="section-label">${className(cls)} — ${t('{n} dáng', '{n} looks', { n: forms.length })}${chosen ? ` · ${t('đang mặc:', 'wearing:')} ${esc(chosenName)}` : ` · ${t('đang mặc dáng gốc', 'wearing the base look')}`}</div>
        ${this.gearPreviewHtml(`${t('Xem trước', 'Preview')} ${className(cls)}${chosen ? ` · ${t('dáng', 'look')} “${esc(chosenName)}”` : ` · ${t('dáng gốc', 'base look')}`}`)}
        ${staleHint}${emptyHint}${baseRow}${formRows}
        <div class="d" style="font-size:11.5px;color:var(--paper-dim)">${t('Dáng chỉ đổi NGOẠI HÌNH (không cộng chỉ số). Mỗi class dùng 1 dáng, áp cho mọi tướng cùng class.', 'A look only changes APPEARANCE (no stat bonus). Each class uses one look, shared by every hero of that class.')}</div>`;
      this.tabBody.querySelectorAll<HTMLElement>('[data-form]').forEach((b) =>
        b.addEventListener('click', () => {
          sound.playClick();
          void g.doSelectForm(cls, b.dataset.form ?? '');
        }),
      );
      void this.drawGearPreview(cls, chosen);
    }

    this.tabBody.querySelectorAll<HTMLElement>('[data-form-class]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.shopClass = b.dataset.formClass as ClassId;
        this.render();
      }),
    );
  }

  // ------------------------------------------------------------ tập luyện


  // ------------------------------------------------------------ sảnh chọn màn

  /**
   * SẢNH CHỌN MÀN — màn hình đầu tiên khi vào trận.
   *
   * Người dùng yêu cầu: "Màn 1.1, 1.2, 1.3… chưa qua 1.1 thì 1.2 bị lock", mỗi màn có
   * easy/ normal/ elite, "qua easy mới được chọn normal, qua normal mới được chọn elite".
   *
   * ⚠️ UI KHÔNG tự kiểm luật khoá: mọi ô đều lấy `unlocked`/`cleared` từ `Game.mapGrid()`
   * (chạy `sim/progress.ts`). Sửa luật chỉ phải sửa 1 chỗ, và test khoá được bằng số đo.
   */
  /**
   * (ĐÃ BỎ) CHUYỂN CHẾ ĐỘ TÁC CHIẾN / QUẢN LÝ TRÊN MOBILE.
   *
   * Người dùng: *"Bỏ tab `Nâng cấp & shop` trong màn hình chiến đấu. Vì đã bỏ tab `Nâng cấp & shop`
   * nên chỉ còn lại tab Tác chiến --> không cần thiết nên không cần phân tab nữa"*.
   *
   * Giữ hàm này dưới dạng NO-OP (không xoá hẳn) vì `showScreen()` và nút đóng drawer cũ vẫn gọi —
   * xoá hàm sẽ phải sửa nhiều chỗ gọi mà không đem lại lợi ích gì. Tham số `view` không còn tác dụng:
   * bàn điều khiển chiến đấu LUÔN hiện (xem CSS: `.battle-section` không còn phụ thuộc
   * `data-mobile-view`).
   */
  setMobileView(_view: 'battle' | 'manage'): void {
    void _view;
  }

  // ------------------------------------------------------------ TRANG CHỦ (ngoài game)

  /**
   * CHUYỂN MÀN HÌNH: `home` (ngoài game) ⇄ `game` (trong trận) ⇄ `arena` (ARENA PVE — BỔ SUNG).
   *
   * ⚠️ Vì sao dùng thuộc tính `data-screen` trên `#app` thay vì ẩn/ hiện từng khối:
   * CSS chỉ cần 2 quy tắc là đủ, và mọi thành phần khác (banner, toast, modal) vẫn nằm
   * trong đúng nhánh của nó ⇒ không có chuyện "đang ở trang chủ mà thấy sân đấu phía sau".
   *
   * ⚠️⚠️ MÀN THỨ BA `'arena'` — ĐỌC KỸ TRƯỚC KHI THÊM NHÁNH:
   * Arena là chế độ BỔ SUNG (người dùng nhấn mạnh: **KHÔNG thay thế game 10-wave**). Nó dùng CHUNG
   * canvas Phaser (repo chỉ có MỘT `Game`, xem `Game.phaser`) nhưng KHÁC HUD và KHÁC luật. Vì vậy:
   *   · CSS hiện `#ingame` (để `#arena` CÓ KÍCH THƯỚC THẬT ⇒ canvas không 0×0) nhưng ẩn khung
   *     10-wave (`#topHud`, `#side`, `#banner`, `#wavebar`) và hiện `#arenaHud` — xem khối
   *     `ARENA PVE` trong `src/style.css`.
   *   · vòng lặp Phaser phải THỨC ở arena (phải vẽ sân), và nhịp `renderLight()` của game TD phải
   *     NGỦ (không có wave/ vàng TD để vẽ).
   *   · `which === 'game'` KHÔNG được đổi hành vi: nhánh cũ giữ NGUYÊN.
   */
  showScreen(which: 'home' | 'game' | 'arena'): void {
    const app = document.getElementById('app');
    if (app) app.dataset.screen = which;
    this.screen = which;
    if (which === 'game') this.setMobileView('battle');
    /*
     * ⚠️ `#wavebar` PHẢI được ẩn hẳn khi ở trang chủ.
     * Lỗi đã dính: nó chỉ bị che về hình chứ `display` vẫn `flex` (đo được
     * `wavebarHidden:false, display:flex`) ⇒ vẫn nằm trong cây DOM đang vẽ, và nhịp 8 lần/giây
     * còn ghi lại nội dung của nó. Ở trang chủ nó vô nghĩa (không có wave nào).
     */
    if (which === 'home') this.wavebar.classList.add('hidden');
    /*
     * ⚠️ BẮT BUỘC: ÉP PHASER ĐO LẠI KÍCH THƯỚC SAU KHI ĐỔI MÀN HÌNH.
     *
     * Lỗi đã dính (đo được): lúc boot `#home` đang hiện nên `#arena` có kích thước 0×0 ⇒
     * Phaser dựng canvas **0×0** và KHÔNG tự đo lại (vì cửa sổ không đổi kích thước). Vào
     * trận thì `#arena` đã 1576×404 nhưng canvas vẫn 0×0 VĨNH VIỄN ⇒ sân trống và MỌI CÚ
     * BẤM TRÊN SÂN KHÔNG ĂN (không ra lệnh di chuyển, không chọn được tướng).
     * Gọi `scale.refresh()` sau khi đổi màn hình để Phaser đo lại đúng khung chứa.
     */
    /*
     * ĐO LẠI KÍCH THƯỚC SÂN — nhiều nhịp, KHÔNG chỉ một lần.
     *
     * ⚠️ Vì sao phải lặp: lúc boot khung `#arena` bị ẩn (0×0) nên Phaser đặt canvas CSS 0×0.
     * Sau khi đổi màn hình, layout cần vài nhịp mới ổn định (khác nhau theo hướng màn hình:
     * màn NGANG điện thoại 844×390 còn trễ hơn màn dọc). Chỉ đo 1–2 nhịp là có lúc vẫn 0×0
     * ⇒ sân TRỐNG và mọi cú chạm không ăn (đo được: landscape canvas w=0).
     * Cách chắc ăn: thử lại trong ~1 giây, dừng ngay khi đo được kích thước thật.
     */
    let tries = 0;
    const poll = (): void => {
      this.game.resizeArena();
      const host = document.getElementById('arena');
      const ok = !!host && host.clientWidth > 1 && host.clientHeight > 1;
      const canvas = document.querySelector<HTMLCanvasElement>('#arena canvas');
      const sized = !!canvas && canvas.getBoundingClientRect().width > 1;
      if ((ok && sized) || ++tries > 60) return; // ~1s ở 60fps
      window.requestAnimationFrame(poll);
    };
    window.requestAnimationFrame(poll);
    /*
     * ⚠️ NGỦ VÒNG LẶP PHASER KHI Ở TRANG CHỦ — đây là sửa chính cho lỗi "miết nhanh bị khựng".
     * Đo được: ngủ vòng lặp ⇒ p95 khung hình 210ms → 83ms, max 2792ms → 93ms.
     *
     * ⚠️ ARENA CŨNG PHẢI THỨC: sân Arena vẽ bằng chính vòng lặp Phaser (`ArenaBattleScene.update`),
     * nên `which === 'game'` cũ phải đổi thành "KHÔNG ở trang chủ". Chỉ để `'game'` thức là vào
     * Arena gặp sân ĐỨNG IM — mà lỗi đó trông như "sim hỏng" nên rất khó lần ra.
     */
    this.game.setLoopAwake(which !== 'home');
    if (which === 'home') {
      this.hideBanner();
      this.renderHome();
    } else if (which === 'arena') {
      /*
       * ⚠️ KHÔNG gọi `this.render()` ở nhánh Arena: `render()` là HUD của trận 10-wave (wave/ vàng
       * TD/ Nhà/ nút Bắt đầu Wave). Gọi nó ở Arena là ghi số của TD vào các ô đang bị CSS ẩn, và
       * tệ hơn: nó đọc `this.game.world` của trận TD ⇒ vẽ một trạng thái không liên quan.
       * HUD Arena do `ArenaHud` (src/ui/hud-arena.ts) lo, `Game` gọi `render()` của nó theo nhịp.
       */
      this.hideBanner();
      this.arenaHud?.show();
    } else {
      this.render();
    }
  }

  /** Vẽ TRANG CHỦ: thẻ màn đang chọn + dấu trang + tài nguyên. */
  renderHome(): void {
    const g = this.game;
    const grid = g.mapGrid();
    const carousel = document.getElementById('mapCarousel');
    const dots = document.getElementById('mapDots');
    if (!carousel) return;

    // tài nguyên
    const gold = document.getElementById('homeGold');
    const gems = document.getElementById('homeGems');
    const heroes = document.getElementById('homeHeroes');
    if (gold) gold.textContent = Math.floor(g.meta.gold).toLocaleString('vi-VN');
    if (gems) gems.textContent = Math.floor(g.meta.diamonds ?? 0).toLocaleString('vi-VN');
    /*
     * v7: badge trang chủ KHÔNG còn là "sổ tướng n/5" (sổ đã xoá). Nay hiện SỐ CLASS ĐANG CHƠI ĐƯỢC
     * — người dùng chốt *"sổ tướng tính theo số lượng class đang active"*.
     */
    if (heroes) heroes.textContent = `${g.meta.unlocked.length}`;

    /*
     * MỖI MÀN = 1 THẺ. Thẻ nào CHƯA MỞ thì vẫn hiện nhưng mờ + 🔒 (người chơi thấy được
     * phía trước để có động lực cày, và biết vì sao không bấm được).
     */
    const maps = [...new Map(grid.map((c) => [c.map.id, c.map])).values()];
    carousel.innerHTML = maps
      .map((map, i) => {
        const cells = grid.filter((c) => c.map.id === map.id);
        const anyUnlocked = cells.some((c) => c.unlocked);
        const allCleared = cells.every((c) => c.cleared);
        const diffs = cells
          .map((c) => {
            const name = diffName(DIFFICULTIES.find((d) => d.id === c.difficulty)!);
            const on = g.meta.currentMap === map.id && g.meta.currentDiff === c.difficulty;
            return `<button class="diff-pill${on ? ' on' : ''}${c.cleared ? ' done' : ''}"
              data-map="${map.id}" data-diff="${c.difficulty}" ${c.unlocked ? '' : 'disabled'}
              title="${c.unlocked ? t('Chơi {map} · {diff}', 'Play {map} · {diff}', { map: map.id, diff: name }) : t('Chưa mở — qua màn trước đã', 'Locked — clear the previous stage first')}">
              ${c.cleared ? '✔ ' : c.unlocked ? '' : '🔒 '}${name}
            </button>`;
          })
          .join('');
        // ⚠️ Không đặt comment HTML trong template literal: `<!--` ... sẽ phá cú pháp `${}`.
        // Ảnh nạp sẵn bằng `Image()` ở `renderHome` (xem chú thích ở đó).
        return `<article class="mapcard${anyUnlocked ? '' : ' locked'}${allCleared ? ' cleared' : ''}" data-idx="${i}" data-mapid="${map.id}">
          <img class="mapcard-art" src="${mapArtSrc(map.id)}" alt="${mapName(map)}" draggable="false"/>
          <div class="mapcard-body">
            <div class="mapcard-head">
              <b class="mapcard-id">${t('Màn {id}', 'Stage {id}', { id: map.id })}</b>
              ${allCleared ? `<span class="mapcard-badge done">${t('Đã qua', 'Cleared')}</span>` : anyUnlocked ? `<span class="mapcard-badge">${t('Mở', 'Open')}</span>` : `<span class="mapcard-badge locked">🔒 ${t('Khoá', 'Locked')}</span>`}
            </div>
            <div class="mapcard-name">${mapName(map)}</div>
            <div class="mapcard-blurb">${mapBlurb(map)}</div>
            <div class="mapcard-diffs">${diffs}</div>
          </div>
        </article>`;
      })
      .join('');

    // dấu trang: mỗi chấm là 1 màn, chấm đậm = màn đang xem
    if (dots) {
      dots.innerHTML = maps
        .map((m, i) => `<button class="dot${g.meta.currentMap === m.id ? ' on' : ''}" data-dot="${i}" title="${t('Màn {id}', 'Stage {id}', { id: m.id })}" aria-label="Màn ${m.id}"></button>`)
        .join('');
      dots.querySelectorAll<HTMLElement>('[data-dot]').forEach((b) =>
        b.addEventListener('click', () => {
          sound.playClick();
          this.scrollMapIntoView(Number(b.dataset.dot));
        }),
      );
    }

    // chọn độ khó / đổi màn
    carousel.querySelectorAll<HTMLElement>('[data-map]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        const ok = this.game.selectMap(b.dataset.map ?? '', (b.dataset.diff ?? 'easy') as never);
        if (ok) this.renderHome();
      }),
    );

    // nhắc rõ đang chơi màn nào + độ khó nào (nút Vào trận ở ngay dưới)
    const hint = document.getElementById('homeEnterHint');
    if (hint) {
      // ⚠️ tên biến KHÁC hàm `diffName()` ở i18n — tránh che nhau
      const diffLabel = diffName(DIFFICULTIES.find((d) => d.id === g.meta.currentDiff)!);
      const cur = maps.find((m) => m.id === g.meta.currentMap);
      const cell = grid.find((c) => c.map.id === g.meta.currentMap && c.difficulty === g.meta.currentDiff);
      hint.innerHTML = cur
        ? t('Sẽ vào <b>Màn {id} · {diff}</b> — {name}{done}', 'Entering <b>Stage {id} · {diff}</b> — {name}{done}', {
            id: cur.id,
            diff: diffLabel,
            name: mapName(cur),
            done: cell?.cleared ? t(' (đã qua)', ' (cleared)') : '',
          })
        : '';
    }

    this.enableMapSwipe(carousel, maps.length);
    this.scrollMapIntoView(maps.findIndex((m) => m.id === g.meta.currentMap));

    /*
     * NẠP SẴN MỌI ẢNH MÀN.
     * ⚠️ Bỏ `loading="lazy"` là CHƯA ĐỦ: trình duyệt vẫn hoãn ảnh ngoài khung nhìn
     * (`complete === false`) ⇒ trượt tới là ô ảnh TRỐNG (đo được 6/15 rồi 12/15).
     * Gọi `decode()` cho từng ảnh để chúng sẵn sàng trước khi người chơi trượt tới.
     * 15 ảnh SVG ~92KB tổng nên nạp hết là rẻ.
     */
    /*
     * ⚠️ `img.decode()` KHÔNG đủ: ảnh ngoài khung nhìn chưa được TẢI (không có request), nên
     * `decode()` resolve ngay mà chẳng nạp gì (đo được vẫn 6/15). Cách chắc chắn: tự tạo
     * một `Image()` cho từng màn rồi gán src — như vậy trình duyệt BẮT BUỘC tải, và khi thẻ
     * hiện ra thì ảnh đã nằm trong cache (hiện tức thì, không trắng).
     */
    /*
     * ⚠️ CHỈ NẠP SẴN MỘT LẦN CHO MỖI URL.
     *
     * LỖI ĐÃ DÍNH (đúng triệu chứng người dùng: bấm lung tung ở trang chủ rồi treo máy + crash
     * trên điện thoại): `renderHome()` chạy LẠI mỗi lần chọn màn/ đổi độ khó/ đóng panel, mà
     * mỗi lần lại tạo 15 `new Image()` rồi `push` vào `mapArtWarm` — mảng phình VÔ HẠN, ảnh
     * được giữ tham chiếu nên KHÔNG bao giờ được GC. Bấm vài chục lần là hàng trăm ảnh bị giữ.
     * Nay: mỗi URL chỉ nạp một lần, và chỉ giữ tham chiếu của ảnh CHƯA tải xong; tải xong thì
     * bỏ khỏi mảng (trình duyệt đã cache, không cần giữ).
     */
    for (const m of maps) {
      const url = mapArtSrc(m.id);
      if (this.mapArtWarmed.has(url)) continue;
      this.mapArtWarmed.add(url);
      const pre = new Image();
      pre.onload = pre.onerror = () => {
        // tải xong (hoặc lỗi) ⇒ không cần giữ tham chiếu nữa
        const i = this.mapArtWarm.indexOf(pre);
        if (i >= 0) this.mapArtWarm.splice(i, 1);
      };
      pre.src = url;
      this.mapArtWarm.push(pre);
    }

    /*
     * Nâng cấp sang ảnh AI nếu người dùng đã sinh ảnh — CHỈ CHẠY MỘT LẦN.
     * Mỗi lần chạy là 15 request HEAD; `renderHome()` chạy lại mỗi lần chọn màn/ đổi độ khó
     * nên gọi ở đây mỗi lượt là bắn request liên tục từ điện thoại (góp phần làm treo máy).
     * `mapArt.ts` đã nhớ kết quả dò, nên chỉ cần một lượt là đủ cho cả phiên chơi.
     */
    if (!this.mapArtProbed) {
      this.mapArtProbed = true;
      void this.upgradeHomeArt(maps.map((m) => m.id));
    }
  }

  /** Thay ảnh SVG bằng ảnh AI cho màn nào đã có `art/ai/map-<id>.png`. */
  /**
   * NÂNG ẢNH MÀN TỪ SVG LÊN ẢNH AI, CÓ TIẾN TRÌNH.
   *
   * Người dùng: *"khi lần đầu vào màn hình home, đang là ảnh svg và 1 lúc sau home mới load ảnh màn
   * lên"* ⇒ nay màn chờ giữ người chơi lại tới khi ảnh thật sẵn sàng, thanh tiến trình chạy theo
   * ĐÚNG số màn đã nạp (không ước lượng theo thời gian).
   */
  private async upgradeHomeArt(mapIds: string[]): Promise<void> {
    const done = beginTask();
    setProgress(0, t('Đang nạp ảnh màn chơi…', 'Loading stage artwork…'));
    try {
      let i = 0;
      for (const id of mapIds) {
        const src = await upgradeMapArt(id);
        i++;
        // tiến trình theo SỐ MÀN đã xử lý — màn nào không có ảnh AI cũng tính là 1 bước (đã xong)
        setProgress((i / Math.max(1, mapIds.length)) * 100);
        if (!src) continue;
        const img = document.querySelector<HTMLImageElement>(`.mapcard[data-mapid="${id}"] .mapcard-art`);
        if (img) img.src = src;
      }
    } finally {
      done();
    }
  }

  /**
   * TRƯỢT NGANG ĐỂ ĐỔI MÀN — đúng yêu cầu người dùng:
   * "chỗ chọn màn trượt ngang (VD: 1.1 vuốt từ phải sang trái sẽ sang 1.2, 1.3)".
   *
   * Dùng scroll-snap của CSS làm phần "trượt", còn ở đây chỉ:
   *   · kéo bằng chuột (desktop không có vuốt),
   *   · nút ‹ › để chắc chắn bấm được (không phụ thuộc cử chỉ),
   *   · cập nhật chấm trang theo vị trí cuộn.
   */
  private enableMapSwipe(carousel: HTMLElement, count: number): void {
    /*
     * Nút ‹ › đi ĐÚNG 1 THẺ, không phải "1 màn hình".
     * ⚠️ Bẫy đã dính: dùng `scrollBy({behavior:'smooth'})` liên tiếp thì các animation XẾP HÀNG
     * và bấm ‹ lại chạy tiếp về phía trước (đo được: 2322 → 3382 sau khi bấm lùi). Vì vậy:
     * cuộn theo CHỈ SỐ THẺ bằng `scrollTo` không animation, rồi cập nhật chấm ngay.
     */
    const cards = [...carousel.querySelectorAll<HTMLElement>('.mapcard')];
    /*
     * ⚠️ TÍNH VỊ TRÍ THẺ THEO `getBoundingClientRect`, KHÔNG theo `offsetLeft`.
     * `offsetLeft` tính từ `offsetParent` — mà `offsetParent` ở đây KHÔNG phải carousel
     * (phần tử cha gần nhất có position khác static), nên hiệu `card.offsetLeft -
     * carousel.offsetLeft` sai lệch (đo được: bấm ‹ không lùi, chấm lệch 1 ô).
     * Rect là toạ độ màn hình thật ⇒ quy đổi về scrollLeft bằng hiệu với rect của carousel.
     */
    const leftOfCard = (card: HTMLElement): number => {
      const cr = carousel.getBoundingClientRect();
      const br = card.getBoundingClientRect();
      return carousel.scrollLeft + (br.left - cr.left);
    };
    const targetLeft = (idx: number): number => {
      const card = cards[Math.max(0, Math.min(cards.length - 1, idx))];
      return card ? Math.max(0, leftOfCard(card) - 8) : 0;
    };
    /*
     * "THẺ ĐANG XEM" = thẻ có MÉP TRÁI gần mép trái khung nhất.
     *
     * ⚠️ Bẫy đã dính: lấy thẻ ở GIỮA khung thì khi `scrollLeft = 0` thẻ giữa là thẻ số 1–2
     * (vì thẻ 0 nằm sát mép trái, không ở giữa) ⇒ chấm sáng lệch 1–2 ô và nút ‹ không lùi được
     * (đo được: ở left=0 chấm sáng #1, "thẻ giữa" là 1.2). Với danh sách dạng băng thì mốc
     * đúng là MÉP TRÁI, không phải tâm.
     */
    const currentIdx = (): number => {
      const cr = carousel.getBoundingClientRect();
      let best = 0;
      let bestD = Infinity;
      cards.forEach((c, i) => {
        const br = c.getBoundingClientRect();
        const d = Math.abs(br.left - cr.left);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    };
    const prev = document.getElementById('btnMapPrev');
    const next = document.getElementById('btnMapNext');
    // gán lại listener mỗi lần vẽ: `onclick` ghi đè nên không bị chồng nhiều handler
    if (prev) {
      (prev as HTMLElement).onclick = () => {
        sound.playClick();
        this.setCarouselLeft(carousel, targetLeft(currentIdx() - 1));
      };
    }
    if (next) {
      (next as HTMLElement).onclick = () => {
        sound.playClick();
        this.setCarouselLeft(carousel, targetLeft(currentIdx() + 1));
      };
    }

    // kéo bằng chuột (desktop): giữ chuột trái rồi kéo ngang
    let down = false;
    let startX = 0;
    let startScroll = 0;
    carousel.onpointerdown = (ev) => {
      if (ev.pointerType === 'touch') return; // cảm ứng đã có scroll native
      down = true;
      startX = ev.clientX;
      startScroll = carousel.scrollLeft;
      carousel.classList.add('dragging');
    };
    carousel.onpointermove = (ev) => {
      if (!down) return;
      carousel.scrollLeft = startScroll - (ev.clientX - startX);
    };
    const up = () => {
      if (!down) return;
      down = false;
      carousel.classList.remove('dragging');
      this.snapToNearestCard(carousel);
    };
    carousel.onpointerup = up;
    carousel.onpointercancel = up;
    carousel.onpointerleave = up;

    // cuộn xong thì cập nhật chấm
    let t: number | null = null;
    carousel.onscroll = () => {
      if (t !== null) window.clearTimeout(t);
      t = window.setTimeout(() => this.syncHomeDots(carousel), 90);
    };
    void count;
  }

  /** Cuộn tức thì tới vị trí `left` rồi đồng bộ chấm (không dùng animation để khỏi xếp hàng). */
  private setCarouselLeft(carousel: HTMLElement, left: number): void {
    carousel.scrollTo({ left, behavior: 'auto' });
    this.syncHomeDots(carousel);
  }

  /** Cuộn tới thẻ màn thứ `idx` (dùng cho chấm trang & lúc mở trang chủ). */
  private scrollMapIntoView(idx: number): void {
    const carousel = document.getElementById('mapCarousel');
    if (!carousel || idx < 0) return;
    const card = carousel.querySelector<HTMLElement>(`.mapcard[data-idx="${idx}"]`);
    if (!card) return;
    const cr = carousel.getBoundingClientRect();
    const br = card.getBoundingClientRect();
    this.setCarouselLeft(carousel, Math.max(0, carousel.scrollLeft + (br.left - cr.left) - 8));
  }

  /** Thẻ nào đang ở giữa khung ⇒ chấm đó sáng. */
  private syncHomeDots(carousel: HTMLElement): void {
    const cards = [...carousel.querySelectorAll<HTMLElement>('.mapcard')];
    if (!cards.length) return;
    // cùng lý do như ở `enableMapSwipe`: mốc là MÉP TRÁI, dùng rect (không dùng offsetLeft)
    const cr = carousel.getBoundingClientRect();
    let best = 0;
    let bestD = Infinity;
    cards.forEach((c, i) => {
      const br = c.getBoundingClientRect();
      const d = Math.abs(br.left - cr.left);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    document.querySelectorAll<HTMLElement>('#mapDots .dot').forEach((d, i) => d.classList.toggle('on', i === best));
  }

  /** Kéo xong thì hít vào thẻ gần nhất (để không dừng ở nửa thẻ). */
  private snapToNearestCard(carousel: HTMLElement): void {
    const cards = [...carousel.querySelectorAll<HTMLElement>('.mapcard')];
    if (!cards.length) return;
    const cr = carousel.getBoundingClientRect();
    let best = cards[0];
    let bestD = Infinity;
    for (const c of cards) {
      const br = c.getBoundingClientRect();
      const d = Math.abs(br.left - cr.left);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    const br = best.getBoundingClientRect();
    this.setCarouselLeft(carousel, Math.max(0, carousel.scrollLeft + (br.left - cr.left) - 8));
  }

  /**
   * MỞ 1 CHỨC NĂNG Ở TRANG CHỦ: Shop · Tập luyện · Trang phục · Kỹ năng.
   *
   * Vì sao dùng lại `tabBody` (vốn của khối trong game): 4 màn này chỉ là CÙNG một bộ
   * renderer, khác chỗ hiển thị. Dựng thêm 4 màn riêng nữa là nhân đôi code và sớm muộn
   * cũng lệch nhau. Ở đây chỉ đổi `game.tab` rồi hiện panel nổi trên trang chủ.
   */
  openHomeFeature(tab: 'shop' | 'train' | 'gear' | 'skill' | 'bag' | 'arena'): void {
    /*
     * ⚠️⚠️ ARENA RẼ NHÁNH Ở ĐÂY — TRƯỚC MỌI THỨ KHÁC, VÀ CỐ Ý KHÔNG MỞ `#homePanel`.
     *
     * Vì sao Arena KHÔNG dùng khuôn mẫu panel của 5 nút kia (dù nó cũng là `data-home`): 5 tab kia
     * là DANH SÁCH trong drawer (mua/ nâng cấp/ chọn) nên ở lại trang chủ là đúng. Arena là một
     * TRẬN ĐẤU có camera cuộn ngang + HUD riêng + vòng lặp Phaser — nhét vào drawer là vừa chật,
     * vừa phải ẩn/ hiện canvas bằng tay (đúng loại chắp vá sinh lỗi "đang ở nhà mà thấy quái chạy").
     * Vì vậy nút Arena đổi sang MÀN HÌNH THỨ BA (`showScreen('arena')`) và `Game` lo phần dựng trận.
     *
     * ⚠️ Chặn TRƯỚC khi đặt `g.tab = tab`: `Game.tab` là union KHÔNG có `'arena'`, gán vào là vừa
     * sai kiểu vừa để lại một giá trị rác cho mọi `renderTabBody()` sau này (panel sẽ rơi vào
     * nhánh `default` và hiện nội dung của tab khác).
     *
     * ⚠️ Vẫn gọi `sound.playClick()`: nút này là nút trang chủ như 5 nút kia, tiếng bấm phải giống
     * nhau. `closeHomePanel()` đóng drawer đang mở (nếu người chơi vừa xem Shop rồi bấm Arena) —
     * để drawer mở mà nhảy sang màn khác là trạng thái nửa vời.
     */
    if (tab === 'arena') {
      this.closeHomePanel();
      sound.playClick();
      this.game.startArenaFromHome();
      return;
    }
    const g = this.game;
    g.tab = tab;
    const panel = document.getElementById('homePanel');
    const title = document.getElementById('homePanelTitle');
    const body = document.getElementById('homePanelBody');
    if (!panel || !body) return;
    const names: Record<string, string> = {
      shop: t('💎 Shop — mua bằng kim cương', '💎 Shop — spend diamonds'),
      train: t('🥋 Tập luyện — cấp vĩnh viễn & Phòng Thời gian', '🥋 Training — permanent levels & Time room'),
      gear: t('👘 Trang phục — chọn dáng theo skill của class', '👘 Wardrobe — pick a look per class skill'),
      skill: t('📜 Kỹ năng — ghép mảnh & xếp 7 ô chiêu', '📜 Skills — craft shards & set 7 slots'),
      bag: t('🎒 Túi đồ — mảnh class: mở khoá, nâng cấp, bán', '🎒 Bag — class shards: unlock, upgrade, sell'),
    };
    if (title) title.textContent = names[tab] ?? tab;
    /*
     * ⚠️ ĐỔI CÁCH DỰNG: renderer ghi THẲNG vào panel, không qua `tabBody` ẩn rồi copy innerHTML.
     *
     * Lỗi đã dính (đúng triệu chứng người dùng: "đổi qua đổi lại liên tục, click lại chức năng
     * nào đó bị treo 1 lúc sau mới hiển thị"): cách cũ chạy `renderTabBody()` (dựng HTML vào
     * sidebar TRONG GAME đang `display:none`) rồi `body.innerHTML = this.tabBody.innerHTML`
     * (phân tích lại toàn bộ HTML lần hai) — DOM bị dựng và parse HAI LẦN cho mỗi lần mở.
     * Đo được: 1 cú bấm trong panel làm `openHomeFeature` chạy **2 lần** (mỗi lần lại 2 lượt
     * dựng + gắn sự kiện), và độ trễ vẽ panel p95 lên **581ms**.
     * Nay renderer ghi thẳng vào `#homePanelBody`: một lượt dựng, một lượt parse.
     */
    this.homePanelActive = true;
    /*
     * ⚠️ GIỮ `panelBodyOverride` TRONG SUỐT THỜI GIAN PANEL MỞ (xem `closeHomePanel`).
     *
     * Lỗi đã dính: mở panel rồi bấm "Luyện" ⇒ handler gọi `doTrain()` → `hud.render()` →
     * `renderTabBody()`; nhưng lúc đó `panelBodyOverride` đã bị xoá nên HTML được ghi vào
     * `#tabBody` TRONG GAME (đang ẩn) ⇒ PANEL ĐANG MỞ KHÔNG CẬP NHẬT, người chơi bấm Luyện mà
     * vẫn thấy cấp cũ/ vàng cũ.
     * Nay: chừng nào panel còn mở thì mọi lần render đều nhắm vào `#homePanelBody`.
     */
    this.panelBodyOverride = body;
    this.renderTabBody();
    this.bindHomePanelEvents(tab, body);
    panel.classList.remove('hidden');
    /*
     * Nạp ảnh head SAU khi panel đã hiện (xem `warmClassHeads` — dò bên trong renderer sẽ bị lần
     * dựng sau ghi đè, đo được: lần mở đầu tiên mất hết ảnh head).
     */
    if (tab === 'bag' || tab === 'shop') this.warmClassHeads(tab, body);
    /*
     * Cờ `panel-open` chỉ để GIẢM OPACITY vài khối nhỏ của trang chủ (không ẩn, không phủ nền)
     * — xem chú thích dài trong `style.css` về việc lớp phủ toàn màn hình gây giật 0,6 giây.
     */
    document.getElementById('app')?.classList.add('panel-open');
    sound.playClick();
  }

  /**
   * Gắn lại sự kiện cho panel trang chủ.
   *
   * ⚠️ Vì sao phải gắn LẠI chứ không dùng chung listener: `renderShopGems()`… gắn sự kiện vào
   * chính các nút trong `tabBody` bằng closure. Copy HTML sang panel khác là mất sạch handler.
   * Cách chắc chắn: gắn theo `data-*` y hệt, gọi đúng hàm xử lý của game.
   */
  private bindHomePanelEvents(tab: string, body: HTMLElement): void {
    const g = this.game;
    /*
     * ⚠️ MỘT ĐƯỜNG LÀM MỚI DUY NHẤT.
     * Bản cũ gọi CẢ `renderHome()` (dựng lại 15 thẻ màn + ảnh) LẪN `openHomeFeature()` (dựng lại
     * panel + gắn lại toàn bộ sự kiện) cho mỗi thao tác ⇒ gấp đôi công việc, và khi người chơi
     * bấm liên tục thì xếp hàng thành khựng.
     * Nay: chỉ vẽ lại SỐ LIỆU của trang chủ (thanh tài nguyên) + panel đang mở.
     */
    const done = (ok: boolean, msg: string): void => {
      this.toast(msg);
      if (ok) {
        g.persist();
        this.refreshHomeNumbers();
        this.openHomeFeature(tab as never); // vẽ lại panel với số liệu mới
      }
    };
    body.querySelectorAll<HTMLElement>('[data-buy]').forEach((b) =>
      b.addEventListener('click', () => {
        const r = buyShopItem(g.meta, b.dataset.buy!);
        done(r.ok, simMsg(r.message));
      }),
    );
    body.querySelectorAll<HTMLElement>('[data-craft]').forEach((b) =>
      b.addEventListener('click', () => {
        const r = craftSkill(g.meta, g.shopClass, b.dataset.craft!);
        done(r.ok, simMsg(r.message));
      }),
    );
    /*
     * `[data-equip]` = TRANG BỊ CHIÊU (ô chiêu). Trước đây nút này dùng chung cho cả phụ kiện
     * nên phải phân biệt bằng `tab`; PHỤ KIỆN ĐÃ XOÁ HẲN ⇒ chỉ còn một đường.
     */
    body.querySelectorAll<HTMLElement>('[data-equip]').forEach((b) =>
      b.addEventListener('click', () => {
        const eq = equippedSkills(g.meta, g.shopClass);
        const r = equipSkill(g.meta, g.shopClass, b.dataset.equip!, Math.min(eq.length, MAX_EQUIPPED_SKILLS - 1));
        done(r.ok, simMsg(r.message));
      }),
    );
    body.querySelectorAll<HTMLElement>('[data-unequip]').forEach((b) =>
      b.addEventListener('click', () => {
        const r = unequipSkill(g.meta, g.shopClass, Number(b.dataset.unequip));
        done(r.ok, simMsg(r.message));
      }),
    );
    /*
     * ⚠️ ĐÃ BỎ 5 KHỐI GẮN SỰ KIỆN TRÙNG Ở ĐÂY (`data-train`, `data-skill-class`,
     * `data-shop-class`, `data-buyacc`, `data-hero-slot`).
     *
     * Lý do: các hàm render của chính game (`renderTrain`, `renderSkill`, `renderShopGems`,
     * `renderGear`) ĐÃ gắn sự kiện cho đúng những nút đó. Vì `openHomeFeature` nay ghi THẲNG
     * vào panel (không copy HTML nữa), các handler đó vẫn còn nguyên ⇒ gắn thêm lần nữa là
     * MỘT CÚ BẤM CHẠY HAI LẦN. Đo được: bấm 1 nút trong panel ⇒ `openHomeFeature` chạy 2 lần,
     * mỗi lần dựng lại panel + gắn lại toàn bộ sự kiện ⇒ bấm liên tục thì khựng.
     *
     * Ở đây chỉ còn những nút do CHÍNH PANEL sinh ra mà renderer không biết về (`data-buy`,
     * `data-craft`, `data-equip`, `data-unequip`) — xem các khối phía trên.
     */
  }

  /** Đóng panel chức năng ở trang chủ. */
  closeHomePanel(): void {
    document.getElementById('homePanel')?.classList.add('hidden');
    document.getElementById('app')?.classList.remove('panel-open');
    this.homePanelActive = false;
    this.panelBodyOverride = null; // hết mở panel ⇒ render quay về `#tabBody` trong game
  }

  /**
   * CHỈ CẬP NHẬT SỐ LIỆU TRANG CHỦ (vàng/ kim cương/ số tướng) — KHÔNG dựng lại 15 thẻ màn.
   *
   * Dùng sau mỗi thao tác trong panel: dựng lại thẻ màn là việc nặng (15 thẻ + 15 ảnh) mà
   * thao tác mua/ ghép/ luyện KHÔNG làm đổi thẻ màn nào.
   */
  private refreshHomeNumbers(): void {
    const g = this.game;
    const gold = document.getElementById('homeGold');
    const gems = document.getElementById('homeGems');
    const heroes = document.getElementById('homeHeroes');
    if (gold) gold.textContent = Math.floor(g.meta.gold).toLocaleString('vi-VN');
    if (gems) gems.textContent = Math.floor(g.meta.diamonds ?? 0).toLocaleString('vi-VN');
    /*
     * v7: badge trang chủ KHÔNG còn là "sổ tướng n/5" (sổ đã xoá). Nay hiện SỐ CLASS ĐANG CHƠI ĐƯỢC
     * — người dùng chốt *"sổ tướng tính theo số lượng class đang active"*.
     */
    if (heroes) heroes.textContent = `${g.meta.unlocked.length}`;
  }

  /**
   * VÀO TRẬN TỪ TRANG CHỦ.
   *
   * Người dùng: *"khi vào game, load xong ảnh mới hiện màn hình chiến đấu"* ⇒ nếu ảnh AI của trận
   * CHƯA nạp xong (chỉ xảy ra ở trận đầu, khi mạng chậm) thì giữ màn chờ tới khi xong rồi mới hiện.
   *
   * ⚠️ Ảnh AI nạp MỘT LẦN cho cả phiên (trong `onBoot`) ⇒ các trận sau `awaitArtReady()` trả về ngay,
   * KHÔNG hiện màn chờ thừa (nếu hiện thì mỗi lần bấm "VÀO TRẬN" lại chớp màn hình — rất khó chịu).
   */
  startFromHome(): void {
    this.hideBanner();
    const go = (): void => {
      this.showScreen('game');
      this.game.beginRun();
    };
    if (this.game.isArtReady()) {
      go();
      return;
    }
    const done = beginTask();
    setProgress(0, t('Đang nạp hình trận đấu…', 'Loading battle art…'));
    void this.game.awaitArtReady().finally(() => {
      done();
      go();
    });
  }

  /** Về TRANG CHỦ từ trong trận (giữ nguyên tiến trình wave). */
  goHome(): void {
    this.hideBanner();
    this.showScreen('home');
  }

  // ------------------------------------------------------------ kỹ năng (ghép + 7 ô)

  /**
   * TAB KỸ NĂNG — ghép mảnh thành chiêu mới và xếp tối đa 7 ô cho class đang xem.
   * Vì sao tách khỏi tab Mua tướng: đây là chỗ tiêu MẢNH CHIÊU và đổi bộ chiêu ra trận;
   * trộn vào tab mua tướng thì người chơi không biết mình đang tiêu gì.
   */
  private renderSkill(): void {
    const g = this.game;
    const cls = g.shopClass;
    const pool = skillPool(cls);
    const equipped = equippedSkills(g.meta, cls);
    const owned = new Set([...equipped.map((s) => s.id), ...(g.meta.craftedSkills[cls] ?? [])]);
    const shards = g.meta.shards.skill[cls] ?? 0;

    const slots = equipped
      .map(
        (sk, i) => `<div class="skillslot">
          <div class="t">${i + 1}. ${skillName(sk)}</div>
          <div class="d">${sk.manaCost} mana · ${t('hồi', 'cd')} ${sk.cooldown}s<br/>${skillDesc(sk)}</div>
          <button class="btn tiny" data-unequip="${i}">${t('Bỏ', 'Remove')}</button>
        </div>`,
      )
      .join('');

    const craftable = craftableSkills(cls)
      .map((sk) => {
        const have = (g.meta.craftedSkills[cls] ?? []).includes(sk.id);
        return `<div class="rowitem">
          <div>
            <div class="t">${skillName(sk)}</div>
            <div class="d">${sk.manaCost} mana · ${t('hồi', 'cd')} ${sk.cooldown}s · ${skillDesc(sk)}</div>
          </div>
          ${have ? `<span class="tag">${t('Đã ghép', 'Crafted')}</span>` : `<button class="btn primary" data-craft="${sk.id}">${t('Ghép {n} mảnh', 'Craft {n} shards', { n: SHARD_COST.skill })}</button>`}
        </div>`;
      })
      .join('');

    const allPool = pool
      .map((sk) => {
        const isOn = equipped.some((e) => e.id === sk.id);
        const canEquip = owned.has(sk.id) && !isOn;
        return `<div class="rowitem">
          <div>
            <div class="t">${skillName(sk)}</div>
            <div class="d">${sk.manaCost} mana · ${t('hồi', 'cd')} ${sk.cooldown}s · ${skillDesc(sk)}</div>
          </div>
          ${
            isOn
              ? `<span class="tag">${t('Đang dùng', 'Equipped')}</span>`
              : canEquip
                ? `<button class="btn" data-equip="${sk.id}">${t('Trang bị', 'Equip')}</button>`
                : `<span class="tag dim">${t('Chưa có', 'Not owned')}</span>`
          }
        </div>`;
      })
      .join('');

    const chips = CLASS_ORDER.map(
      (c) => `<button class="chip${cls === c ? ' active' : ''}" data-skill-class="${c}">${className(c)}</button>`,
    ).join('');

    this.tabBody.innerHTML = `
      <div class="chips">${chips}</div>
      <div class="section-label">${t('Bộ chiêu ra trận — tối đa {n} ô', 'Battle skill set — max {n} slots', { n: MAX_EQUIPPED_SKILLS })} (${equipped.length}/${MAX_EQUIPPED_SKILLS})</div>
      <div class="skillslots">${slots}</div>
      <div class="section-label">${t('Mảnh chiêu', 'Skill shards')} ${className(cls)}: <b>${shards}</b> · ${t('cần {n} để ghép', 'need {n} to craft', { n: SHARD_COST.skill })}</div>
      ${craftable}
      <div class="section-label">${t('Kho chiêu của', 'Skill vault of')} ${className(cls)} (${t('{n} chiêu', '{n} skills', { n: pool.length })})</div>
      ${allPool}`;

    this.tabBody.querySelectorAll<HTMLElement>('[data-skill-class]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.shopClass = b.dataset.skillClass as ClassId;
        this.render();
      }),
    );
    this.tabBody.querySelectorAll<HTMLElement>('[data-craft]').forEach((b) =>
      b.addEventListener('click', () => {
        const id = b.dataset.craft!;
        const r = craftSkill(g.meta, g.shopClass, id);
        sound.playClick();
        this.toast(simMsg(r.message));
        if (r.ok) {
          g.persist();
          this.render();
        }
      }),
    );
    this.tabBody.querySelectorAll<HTMLElement>('[data-equip]').forEach((b) =>
      b.addEventListener('click', () => {
        const id = b.dataset.equip!;
        const nextIdx = equippedSkills(g.meta, g.shopClass).length;
        const r = equipSkill(g.meta, g.shopClass, id, Math.min(nextIdx, MAX_EQUIPPED_SKILLS - 1));
        sound.playClick();
        this.toast(simMsg(r.message));
        if (r.ok) {
          g.persist();
          this.render();
        }
      }),
    );
    this.tabBody.querySelectorAll<HTMLElement>('[data-unequip]').forEach((b) =>
      b.addEventListener('click', () => {
        const r = unequipSkill(g.meta, g.shopClass, Number(b.dataset.unequip));
        sound.playClick();
        this.toast(simMsg(r.message));
        if (r.ok) {
          g.persist();
          this.render();
        }
      }),
    );
  }

  // ------------------------------------------------------------ shop kim cương

  /**
   * TÊN + MÔ TẢ MÓN SHOP theo ngôn ngữ đang chọn.
   *
   * Tên/ mô tả gốc nằm trong `data/diamond-shop.ts` (DỮ LIỆU tiếng Việt, không dịch tại chỗ).
   * Ở đây tra theo `id` + `kind`/`target` để dựng lại câu tiếng Anh — nhờ vậy thêm class/ chiêu
   * mới là shop tự có bản dịch, không phải chép tay 25 món vào bảng rời.
   */
  private shopItemText(it: ShopItem): { name: string; desc: string } {
    /*
     * `target` mang hai loại id khác nhau tuỳ `kind` (xem `data/diamond-shop.ts`):
     *   · shardHero / shardSkill / hero → id CLASS
     *   · skill                          → id CHIÊU (class sở hữu phải tra qua `classOfSkill`)
     * ⚠️ Từng dùng `classOfSkill` cho MỌI kind nên túi mảnh hiện "Hero shard pouch · " trống tên.
     */
    const isClassTarget = it.kind !== 'skill';
    const owner = isClassTarget ? (it.target as ClassId | undefined) : it.target ? classOfSkill(it.target) : undefined;
    const cls = owner ? className(owner) : '';
    switch (it.kind) {
      case 'shardClass':
        /*
         * ⚠️ NGƯỜI DÙNG CHỐT: *"ở shop đều phải thể hiện đơn vị (Ví dụ: Giá cho 25 mảnh là x kim
         * cương thì phải có số 25 ở mảnh để người chơi biết)"*.
         * Vì vậy TÊN có SỐ MẢNH trong túi, MÔ TẢ có SỐ MẢNH cần để mở khoá/ nâng cấp + giá.
         */
        return {
          name: t('Túi {n} mảnh {cls}', '{n} {cls} shards pack', { n: it.amount ?? 0, cls }),
          desc: t(
            '{n} mảnh class {cls} — {cost}💎 · mở khoá class cần {unlock}, nâng cấp tướng cần {up}',
            '{n} {cls} class shards — {cost}💎 · unlocking the class needs {unlock}, upgrading a hero needs {up}',
            { n: it.amount ?? 0, cls, cost: it.cost, unlock: UNLOCK_SHARDS, up: UPGRADE_SHARDS },
          ),
        };
      case 'shardSkill':
        return {
          name: t('Túi mảnh chiêu · {cls}', 'Move shard pouch · {cls}', { cls }),
          desc: t(
            '+{n} mảnh chiêu {cls} (ghép 1 chiêu cần {need})',
            '+{n} {cls} move shards (crafting a move needs {need})',
            { n: it.amount ?? 0, cls, need: SHARD_COST.skill },
          ),
        };
      case 'skill': {
        const sk = it.target ? skillById(it.target) : undefined;
        const label = sk ? skillName(sk) : it.name;
        const desc = sk ? skillDesc(sk) : it.desc;
        return {
          name: owner ? t('{skill} · {cls}', '{skill} · {cls}', { skill: label, cls }) : label,
          desc: t('{desc} · dùng ngay, không cần ghép', '{desc} · usable right away, no crafting needed', { desc }),
        };
      }
      default:
        return { name: it.name, desc: it.desc };
    }
  }

  /** TAB SHOP — tiêu kim cương (thưởng qua màn) để mua mảnh/ chiêu/ nhân vật. */
  private renderShopGems(): void {
    const g = this.game;
    const groups: Array<{ kind: ShopKind; label: string }> = [
      /*
       * Người dùng chốt: *"Shop thay vì mua nhân vật sẽ là mua Class"* ⇒ bỏ nhóm "Nhân vật".
       * Nhóm mảnh nay là MẢNH CLASS (mở khoá class hoặc nâng cấp tướng), và có cả mảnh của class
       * CHƯA mở khoá để người chơi gom dần.
       */
      { kind: 'shardClass', label: t('Mảnh class (mở khoá / nâng cấp)', 'Class shards (unlock / upgrade)') },
      { kind: 'shardSkill', label: t('Mảnh chiêu', 'Skill shards') },
      { kind: 'skill', label: t('Chiêu mới (mua thẳng)', 'New skills (buy outright)') },
    ];
    const body = groups
      .map((group) => {
        const items = DIAMOND_SHOP.filter((i) => i.kind === group.kind)
          .map((it) => {
            const text = this.shopItemText(it);
            return `<div class="rowitem">
              <div>
                <div class="t">${text.name}</div>
                <div class="d">${text.desc}</div>
              </div>
              <button class="btn ${g.meta.diamonds >= it.cost ? 'primary' : ''}" data-buy="${it.id}" ${g.meta.diamonds >= it.cost ? '' : 'disabled'}>
                💎 ${it.cost}
              </button>
            </div>`;
          })
          .join('');
        return `<div class="section-label">${group.label}</div>${items}`;
      })
      .join('');

    this.tabBody.innerHTML = `
      <div class="rowitem">
        <div><div class="t">💎 ${t('Kim cương: {n}', 'Diamonds: {n}', { n: g.meta.diamonds })}</div>
        <div class="d">${t('Kim cương rơi khi qua màn (màn càng cao/ độ khó càng cao càng nhiều).', 'Diamonds drop when you clear a stage (higher stage / higher difficulty pays more).')}</div></div>
      </div>
      ${body}`;

    this.tabBody.querySelectorAll<HTMLButtonElement>('[data-buy]').forEach((b) =>
      b.addEventListener('click', () => {
        const r = buyShopItem(g.meta, b.dataset.buy!);
        sound.playClick();
        this.toast(simMsg(r.message));
        if (r.ok) {
          g.persist();
          this.render();
        }
      }),
    );
  }

  /**
   * TÚI ĐỒ — MẢNH CLASS: MỞ KHOÁ · NÂNG CẤP · BÁN.
   *
   * Người dùng: *"bổ sung `Túi đồ` ở màn hình chính"* và *"người dùng cũng có thể bán mảnh tướng"*
   * (nay là MẢNH CLASS) *"giá bán là 20 vàng"*, kèm *"mua mảnh bằng kim cương"*.
   *
   * Vì sao cần panel này: trước đây mảnh chỉ hiện lẻ tẻ trong tab Kỹ năng và KHÔNG có chỗ nào bán
   * ⇒ mảnh thừa nằm chết trong save. Nay mọi class (KỂ CẢ CHƯA MỞ KHOÁ) đều thấy được số mảnh,
   * trạng thái, và 3 hành động ngay tại chỗ.
   */
  /**
   * NẠP ẢNH HEAD của MỌI class RỒI VẼ LẠI panel ĐÚNG MỘT LẦN.
   *
   * ⚠️ VÌ SAO PHẢI TÁCH RA VÀ GỌI SAU KHI PANEL HIỆN: `renderBag()` chạy đồng bộ bên trong
   * `openHomeFeature()`, mà hàm đó còn ghi `innerHTML` và gắn sự kiện SAU khi renderer trả về ⇒
   * mọi lần vẽ lại phát sinh từ trong `renderBag` đều bị lần dựng sau ghi đè. Đo trên Chrome thật:
   * mở Túi đồ lần ĐẦU ra **0 ảnh head** (7 con dấu), lần 2 mới ra 5 ảnh. Nay: hiện panel trước,
   * rồi mới dò ảnh và vẽ lại 1 lần nếu có class vừa tải xong.
   */
  private warmClassHeads(tab: string, body: HTMLElement): void {
    const classes = Object.keys(CLASSES) as ClassId[];
    /*
     * ⚠️ KHÔNG dùng cờ "chỉ hẹn 1 lần": mỗi class tải xong ở THỜI ĐIỂM KHÁC NHAU, hẹn 1 lần thì chỉ
     * class đầu tiên được vẽ lại (đo được: lần 1 = 1 ảnh, lần 2 = 2 ảnh thay vì 5). Nay mỗi ảnh
     * xong đều xin vẽ lại, nhưng GỘP bằng `setTimeout` nên nhiều ảnh xong cùng lúc chỉ vẽ 1 lần.
     */
    let queued = false;
    const requestRedraw = (): void => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        // chỉ vẽ lại nếu panel VẪN đang mở đúng tab đó (người chơi có thể đã đóng/ đổi tab)
        if (!this.homePanelActive || this.panelBodyOverride !== body || this.game.tab !== tab) return;
        this.renderTabBody();
      }, 0);
    };
    for (const c of classes) {
      // đã biết là KHÔNG có ảnh (2 class mới chưa có art) ⇒ bỏ qua, không hẹn vô ích
      if (classArtCache.get(c) === false) continue;
      if (classArtReady(c)) continue;
      probeClassArt(c, requestRedraw);
    }
  }

  private renderBag(): void {
    const g = this.game;
    /*
     * 7 CLASS: 5 gốc + 2 mới. Dùng `Object.keys(CLASSES)` để panel TỰ có class mới, không phải sửa
     * file này mỗi lần thêm class (đúng quy ước `DIAMOND_SHOP`).
     */
    const allClasses = Object.keys(CLASSES) as ClassId[];
    /*
     * ⚠️ KHÔNG dò ảnh ở đây. Đã đo: `renderBag()` chạy ĐỒNG BỘ ngay trong `openHomeFeature()`, và
     * `openHomeFeature` còn ghi `innerHTML`/gắn sự kiện SAU đó ⇒ mọi lần vẽ lại do callback ảnh
     * kích hoạt đều bị lần dựng sau ghi đè. Kết quả đo được: mở Túi đồ lần 1 = 0 ảnh head, lần 2 =
     * 5 ảnh. Nay việc dò ảnh do `warmClassHeads()` lo, gọi SAU khi panel đã hiện (xem
     * `openHomeFeature`). Ở đây chỉ cần đọc cache — class nào có ảnh thì hiện ảnh ngay.
     */
    const rows = allClasses
      .map((c) => {
        const have = g.meta.classShards[c] ?? 0;
        const unlocked = g.meta.unlocked.includes(c);
        const def = CLASSES[c];
        /*
         * TRẠNG THÁI + MỤC TIÊU nói rõ cần bao nhiêu mảnh nữa — người dùng yêu cầu *"ở shop đều phải
         * thể hiện đơn vị"*, Túi đồ cũng vậy: luôn có con số cụ thể.
         */
        const need = unlocked ? UPGRADE_SHARDS : UNLOCK_SHARDS;
        const label = unlocked
          ? t('Đã mở khoá · nâng cấp tướng cần {n} mảnh', 'Unlocked · upgrading a hero needs {n} shards', { n: need })
          : t('CHƯA mở khoá · cần {n} mảnh để mở', 'LOCKED · needs {n} shards to unlock', { n: need });
        const can = have >= need;
        /*
         * ⚠️ `onReady` BẮT BUỘC: lần đầu mở Túi đồ, ảnh head còn đang tải ⇒ nếu không vẽ lại thì
         * panel giữ con dấu chữ Hán mãi cho tới khi có thao tác khác (đo được: 7/7 ô là con dấu).
         * Chỉ vẽ lại 1 lần cho cả panel, tránh 7 lần dựng lại DOM.
         */
        /*
         * ⚠️ KHÔNG dùng callback để vẽ lại (đã đo: mở Túi đồ lần đầu ra 0 ảnh head dù 12 request
         * ảnh đã bay — callback chạy trong lúc `renderBag()` đang dựng nên bị cờ chống lặp nuốt).
         * Nay dò ảnh TRƯỚC khi dựng (xem `pendingArt` bên dưới) rồi vẽ lại MỘT LẦN.
         */
        const head = classHeadHtml(c, 30);
        return `<div class="bag-row">
          <div class="bag-id">
            <div class="bag-avatar" style="background:linear-gradient(160deg,${hexColor(def.color)},${hexColor(def.accent)})">${head}</div>
            <div>
              <div class="t">${className(c)} <em style="font-style:normal;color:var(--gold)">${t('{n} mảnh', '{n} shards', { n: have })}</em></div>
              <div class="d">${label}</div>
            </div>
          </div>
          <div class="bag-actions">
            ${
              unlocked
                ? `<button class="btn" data-bag-up="${c}" ${can ? '' : 'disabled'}>${t('Nâng cấp ({n} mảnh)', 'Upgrade ({n} shards)', { n: UPGRADE_SHARDS })}</button>`
                : `<button class="btn" data-bag-unlock="${c}" ${can ? '' : 'disabled'}>${t('Mở khoá ({n} mảnh)', 'Unlock ({n} shards)', { n: UNLOCK_SHARDS })}</button>`
            }
            <button class="btn ghost" data-bag-sell="${c}" ${have > 0 ? '' : 'disabled'}>${t('Bán 1 mảnh = {g} vàng', 'Sell 1 shard = {g} gold', { g: SHARD_SELL_GOLD })}</button>
            ${
              have > 1
                ? `<button class="btn ghost" data-bag-sellall="${c}">${t('Bán hết ({n} mảnh = {g} vàng)', 'Sell all ({n} shards = {g} gold)', { n: have, g: have * SHARD_SELL_GOLD })}</button>`
                : ''
            }
          </div>
        </div>`;
      })
      .join('');

    this.tabBody.innerHTML = `
      <div class="section-label">${t('Mảnh class — gom đủ để mở khoá hoặc nâng cấp tướng', 'Class shards — collect to unlock a class or upgrade a hero')}</div>
      ${rows}
      <div class="d" style="font-size:11.5px;color:var(--paper-dim);margin-top:8px">
        ${t(
          'Mua thêm mảnh bằng 💎 ở Shop · bán mảnh ra vàng {g}/mảnh · mở khoá class cần {u} mảnh · nâng cấp tướng cần {p} mảnh (mỗi lần +1 sức mạnh).',
          'Buy more shards with 💎 in the Shop · sell shards for {g} gold each · unlocking a class needs {u} shards · upgrading a hero needs {p} shards (each +1 power).',
          { g: SHARD_SELL_GOLD, u: UNLOCK_SHARDS, p: UPGRADE_SHARDS },
        )}
      </div>`;

    const bind = (sel: string, fn: (c: ClassId) => void): void => {
      this.tabBody.querySelectorAll<HTMLElement>(sel).forEach((b) =>
        b.addEventListener('click', () => {
          const c = b.dataset.bagUnlock ?? b.dataset.bagUp ?? b.dataset.bagSell ?? b.dataset.bagSellall;
          if (!c) return;
          sound.playClick();
          fn(c as ClassId);
        }),
      );
    };
    bind('[data-bag-unlock]', (c) => g.doUnlockClass(c));
    bind('[data-bag-up]', (c) => g.doUpgradeClass(c));
    bind('[data-bag-sell]', (c) => g.doSellShards(c, 1));
    bind('[data-bag-sellall]', (c) => g.doSellShards(c, g.meta.classShards[c] ?? 0));
  }

  private renderTrain(): void {
    const g = this.game;
    /*
     * MENU TẬP LUYỆN CÓ 2 TAB CON — người dùng chốt: *"Để cùng trong menu tập luyện nhưng chia tab
     * ra"*.
     *   · `level`  = Võ Đường: trả vàng theo CẤP, cộng VĨNH VIỄN vào chỉ số class (bảng `training`).
     *   · `time`   = Phòng Thời gian: trả vàng theo GIÂY, cộng dần vào `classPower` (mỗi điểm
     *                +0,5% toàn bộ chỉ số) — trước đây nằm trong trận, nay mang ra trang chủ.
     * Cả hai đều theo CLASS, nên đặt chung một menu là hợp lý.
     */
    const tabs = `<div class="chips">
      <button class="chip${g.trainTab === 'level' ? ' active' : ''}" data-train-tab="level">${t('📈 Tập luyện (cấp)', '📈 Training (levels)')}</button>
      <button class="chip${g.trainTab === 'time' ? ' active' : ''}" data-train-tab="time">${t('⏳ Thời gian (sức mạnh)', '⏳ Time room (power)')}</button>
    </div>`;
    if (g.trainTab === 'time') {
      /*
       * ⚠️ PHẢI RESET `innerHTML` trước khi dựng: `renderTimeRoomInto` dùng `insertAdjacentHTML`
       * (để KHÔNG xoá dãy tab), nên nếu không xoá nội dung cũ thì mỗi lần bấm tab sẽ CHỒNG thêm một
       * dãy tab nữa — đo được: bấm 1 lần ⇒ 4 nút tab thay vì 2.
       */
      this.tabBody.innerHTML = '';
      this.renderTimeRoomInto(this.tabBody, tabs);
      this.bindTrainTabs();
      return;
    }
    const rows = CLASS_ORDER.map((c) => {
      const level = g.meta.training[c];
      const cost = g.trainCostOf(c);
      return `<div class="rowitem">
        <div><div class="t">${className(c)} <em style="font-style:normal;color:var(--gold)">— ${t('Cấp {n}', 'Level {n}', { n: level })}</em></div>
        <div class="d">${t('{role}. Nâng vĩnh viễn toàn bộ tướng thuộc môn phái này.', '{role}. Permanently upgrades every hero of this school.', { role: classRole(c) })}</div></div>
        <div style="text-align:right"><div class="p">${t('{n} vàng', '{n} gold', { n: cost.toLocaleString('vi-VN') })}</div>
        <button class="btn" data-train="${c}" ${g.meta.gold < cost ? 'disabled' : ''}>${t('Luyện', 'Train')}</button></div>
      </div>`;
    }).join('');
    /*
     * ⚠️ `innerHTML =` (không phải insertAdjacentHTML) — ghi ĐÈ toàn bộ nội dung cũ của tab Thời
     * gian. Đo được: nếu không ghi đè thì quay lại tab LEVEL ra panel TRỐNG.
     */
    this.tabBody.innerHTML = `${tabs}<div class="section-label">${t('Võ Đường — Tăng chỉ số môn phái VĨNH VIỄN', 'Training Hall — Permanently boost a school’s stats')}</div>${rows}`;
    this.tabBody.querySelectorAll<HTMLElement>('[data-train]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.doTrain(b.dataset.train as ClassId);
      }),
    );
    this.bindTrainTabs();
  }

  /** Gắn sự kiện cho 2 tab con của menu Tập luyện (dùng chung cho cả 2 nhánh). */
  private bindTrainTabs(): void {
    this.tabBody.querySelectorAll<HTMLElement>('[data-train-tab]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        this.game.trainTab = b.dataset.trainTab === 'time' ? 'time' : 'level';
        /*
         * ⚠️⚠️ PHẢI ĐI QUA `renderTabBody()`, KHÔNG gọi thẳng `renderTrain()`.
         * `renderTabBody()` mới là chỗ TRỎ `this.tabBody` sang `#homePanelBody` khi panel trang chủ
         * đang mở (xem chú thích "ĐÍCH GHI" ở đó). Gọi thẳng `renderTrain()` thì nó ghi vào
         * `#tabBody` — sidebar TRONG GAME đang ẩn ⇒ đo được: bấm tab đổi `trainTab` thành `time`
         * nhưng panel vẫn hiện nguyên Võ Đường, và dãy tab bị nhân đôi (2 → 4 nút).
         */
        this.renderTabBody();
      }),
    );
  }

  // ------------------------------------------------------------ phòng thời gian

  /**
   * PHÒNG THỜI GIAN — nay GẮN THEO CLASS và nằm Ở TRANG CHỦ.
   *
   * Người dùng chốt: *"Sức mạnh trong phòng thời gian chuyển ra ngoài màn hình chính, gắn theo
   * class giống training"*.
   *   · Trước: chọn từng NHÂN VẬT trong sổ để luyện, `timePower` lưu theo nhân vật, và panel nằm
   *     trong trận (tab "Thời gian").
   *   · Nay: chọn CLASS đang mở khoá, `classPower` cộng cho MỌI tướng của class đó (giống cách
   *     `training` hoạt động), và panel nằm ở TRANG CHỦ.
   */
  private renderTimeRoom(): void {
    // tab 'time' cũ trong sidebar TRONG TRẬN (nút đã bỏ, giữ nhánh cho an toàn)
    this.tabBody.innerHTML = '';
    this.renderTimeRoomInto(this.tabBody);
  }

  /**
   * PHÒNG THỜI GIAN — ghi vào ĐÍCH bất kỳ.
   *
   * Người dùng chốt: *"Để cùng trong menu tập luyện nhưng chia tab ra"* ⇒ hàm này được menu Tập
   * luyện gọi để nhúng vào tab con thứ hai, thay vì có panel riêng.
   */
  private renderTimeRoomInto(target: HTMLElement, before = ''): void {
    const g = this.game;
    const session = g.timeRoom;
    const cur = g.timeClass;
    const chip = (c: ClassId): string => {
      const power = g.meta.classPower[c] ?? 0;
      return `<button class="chip${cur === c ? ' active' : ''}" data-time-class="${c}">
        ${className(c)} (+${(power * 0.5).toFixed(1)}%)
      </button>`;
    };
    const options = g.meta.unlocked.map(chip).join('');

    let live = '';
    if (session && !session.done) {
      live = `<div class="timebox">
        <div class="big">${session.secondsLeft.toFixed(1)}s</div>
        <div class="d">${t('Đã tiêu: {gold} vàng · +{pct}% sức mạnh', 'Spent: {gold} gold · +{pct}% power', { gold: Math.round(session.spentGold), pct: (session.powerGained * 0.5).toFixed(1) })}</div>
        <button class="btn ghost" data-time-stop>${t('Dừng', 'Stop')}</button>
      </div>`;
    } else if (session?.done) {
      live = `<div class="timebox"><div class="d">${t('{msg}. Tổng: {gold} vàng, +{pct}% sức mạnh.', '{msg}. Total: {gold} gold, +{pct}% power.', { msg: simMsg(session.message), gold: Math.round(session.spentGold), pct: (session.powerGained * 0.5).toFixed(1) })}</div></div>`;
    }

    /*
     * ⚠️ DÙNG `insertAdjacentHTML('beforeend')`, KHÔNG dùng `innerHTML =` — vì menu Tập luyện đã
     * ghi dãy TAB CON vào `target` TRƯỚC khi gọi hàm này; gán `innerHTML` sẽ XOÁ mất 2 tab đó.
     */
    target.insertAdjacentHTML(
      'beforeend',
      `${before}
      <div class="section-label">${t('Chọn CLASS để tu luyện', 'Pick a class to train')}</div>
      <div class="chips">${options}</div>
      <div class="section-label">${t('Thời gian tu luyện (10 vàng/giây — hết vàng tự dừng)', 'Training time (10 gold/second — stops when gold runs out)')}</div>
      <div class="chips">
        ${[30, 60, 120, 300].map((sec) => `<button class="chip" data-time-sec="${sec}">${t('{s}s ({gold} vàng)', '{s}s ({gold} gold)', { s: sec, gold: (sec * 10).toLocaleString('vi-VN') })}</button>`).join('')}
      </div>
      ${live}
      <div class="d" style="font-size:11.5px;color:var(--paper-dim)">${t('Sức mạnh phòng thời gian cộng cho MỌI tướng của class này, tăng đều mọi chỉ số cơ bản, có giới hạn theo độ khó màn.', 'Time-room power applies to EVERY hero of that class, raising all base stats evenly, capped by stage difficulty.')}</div>`,
    );

    target.querySelectorAll<HTMLElement>('[data-time-class]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.timeClass = b.dataset.timeClass as ClassId;
        this.render();
      }),
    );
    target.querySelectorAll<HTMLElement>('[data-time-sec]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.startTimeRoom(cur, Number(b.dataset.timeSec));
      }),
    );
    target.querySelectorAll<HTMLElement>('[data-time-stop]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        g.stopTimeRoom();
      }),
    );
  }

  // ------------------------------------------------------------ banner / toast / modal

  showBanner(title: string, html: string, buttons: Array<{ label: string; action: () => void }>): void {
    if (title.includes('QUA MÀN')) {
      sound.playVictory();
    } else if (title.includes('THỦNG') || title.includes('THUA')) {
      sound.playDefeat();
    }

    this.banner.innerHTML = `<h2>${title}</h2><p>${html}</p><div class="row"></div>`;
    const row = this.banner.querySelector('.row')!;
    for (const b of buttons) {
      const btn = document.createElement('button');
      btn.className = 'btn primary';
      btn.textContent = b.label;
      btn.addEventListener('click', () => {
        sound.playClick();
        b.action();
      });
      row.appendChild(btn);
    }
    this.banner.classList.remove('hidden');
  }

  hideBanner(): void {
    this.banner.classList.add('hidden');
  }

  toast(msg: string): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.add('show');
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('show'), 2000);
  }

  showHelp(): void {
    this.modalTitle.textContent = t('Bí Kíp Xuất Trận', 'Battle Manual');
    this.modalBody.innerHTML = t(
      `
      <p><b>Mục tiêu tối thượng:</b> Bảo vệ <b>Trụ Nhà</b> ở trung tâm võ đài qua <b>10 wave</b>. Mỗi wave đều có <b>Boss</b> xuất hiện. Nhà hết máu là thất thủ.</p>
      <ul>
        <li><b>Di chuyển tướng:</b> Chọn thẻ tướng ở danh sách, rồi <b>chạm/bấm/kéo</b> trên võ đài để ra lệnh. Tướng sẽ di chuyển đến điểm đó và tự lập vị trí đóng quân.</li>
        <li><b>Tự động ra đòn võ:</b> Tướng tự động xuất chiêu liên hoàn đòn (đấm, móc, đá, chỏ, chưởng) vào kẻ địch đang áp sát Nhà gần nhất.</li>
        <li><b>Tuyệt Chiêu (bấm tay):</b> Mỗi môn phái có <b>1 tuyệt chiêu riêng</b>, chỉ dùng được khi đủ Mana và hồi <b>3 phút</b>. Bấm nút vàng trên thẻ tướng để thi triển.</li>
        <li><b>Chiêu thức (tự niệm):</b> Mỗi môn phái có <b>7 chiêu thức</b> mở dần theo Wave, tướng <b>tự động niệm</b> khi đủ Mana. Vào trận chỉ mang tối đa <b>3 chiêu</b> — chọn ở mục Kỹ năng ngoài trang chủ.</li>
        <li><b>Dược Thần Cứu Cấp:</b>
          <ul>
            <li><b>Bình Máu [1]:</b> Hồi lập tức 50% lượng máu tối đa.</li>
            <li><b>Bình Mana [2]:</b> Hồi đầy 100% cột mana.</li>
            <li><b>Bình Thể Lực [3]:</b> Kích hoạt <b>BẤT TỬ trong 5 giây</b> + tăng 60% sức mạnh (tối đa 2 bình/màn).</li>
          </ul>
        </li>
        <li><b>Tối ưu trên Mobile:</b> Chạm vào thanh <b>⚔️ Tác chiến / 📜 Nâng cấp</b> để chuyển nhanh giữa bàn điều khiển kỹ năng và cửa hàng trang bị. Bật/tắt âm thanh bằng biểu tượng loa ở góc trên.</li>
      </ul>`,
      `
      <p><b>Ultimate goal:</b> Protect the <b>House</b> at the centre of the arena through <b>10 waves</b>. Every wave sends a <b>Boss</b>. If the House runs out of HP, you lose.</p>
      <ul>
        <li><b>Move a hero:</b> Pick a hero card in the list, then <b>tap/click/drag</b> on the arena to give an order. The hero walks there and takes up position.</li>
        <li><b>Auto attacks:</b> Heroes automatically unleash their martial combo (punch, hook, kick, elbow, palm) on the closest enemy pressing the House.</li>
        <li><b>Ultimate (manual):</b> Each class has <b>1 signature ultimate</b>, usable only with enough Mana, on a <b>3-minute</b> cooldown. Tap the gold button on the hero card to cast it.</li>
        <li><b>Skills (auto-cast):</b> Each class has <b>7 skills</b> that unlock as Waves go by; heroes <b>cast them automatically</b> when they have Mana. A run carries at most <b>3</b> — pick them under Skills on the home screen.</li>
        <li><b>Emergency potions:</b>
          <ul>
            <li><b>Health Potion [1]:</b> instantly restores 50% max HP.</li>
            <li><b>Mana Potion [2]:</b> fully refills the mana bar.</li>
            <li><b>Stamina Potion [3]:</b> grants <b>INVULNERABILITY for 5s</b> + 60% power (max 2 per stage).</li>
          </ul>
        </li>
        <li><b>Mobile tips:</b> tap the <b>⚔️ Battle / 📜 Upgrades</b> bar to switch between the skill console and the shop. Toggle sound with the speaker icon in the top corner.</li>
      </ul>`,
    );
    this.modal.classList.remove('hidden');
  }

  /**
   * GIỚI THIỆU CLASS + MÔ TẢ KỸ NĂNG — mở từ nút `?` cạnh "CHIÊU MỘ NHANH".
   *
   * Người dùng: *"cho nút `?` cạnh chữ `Chiêu mộ nhanh`, click vào sẽ ra giới thiệu các class và mô
   * tả skill của các class"*.
   *
   * ⚠️ VÌ SAO DỰNG BẰNG DỮ LIỆU, KHÔNG VIẾT TAY TỪNG CLASS:
   * Toàn bộ nội dung lấy từ `CLASSES` + `classStatsFor()` + `skillName()/skillDesc()`. Thêm class
   * mới, đổi số liệu hay sửa mô tả chiêu trong `data/classes.ts` là bảng này tự đúng — không có
   * bản sao thứ hai để lệch. (Đúng bài học đã dính với `renderRoster`: 2 nơi tự đếm tướng ⇒ lệch.)
   *
   * ⚠️ CHỈ HIỆN CLASS ĐÃ MỞ KHOÁ (`meta.unlocked`): `carpenter`/`diver` có trong `CLASSES` nhưng
   * chưa mở khoá và số liệu còn là TẠM (xem TODO(design) trong `data/classes.ts`). Giới thiệu chúng
   * như class chơi được sẽ gây hiểu sai. Khi nào mở khoá thì tự động xuất hiện, không phải sửa đây.
   *
   * ⚠️ Số liệu dùng `classStatsFor(id, meta)` — TỨC LÀ CHỈ SỐ THẬT của người chơi (đã gồm điểm nâng
   * class), không phải `base` thô. Người chơi mở bảng này để biết class nào mạnh, nên phải thấy số
   * đúng với trận sắp đánh.
   */
  showClasses(): void {
    const meta = this.game.meta;
    // Giữ đúng thứ tự chuẩn của game (`CLASS_ORDER`), chỉ lọc theo đã mở khoá.
    const ids = CLASS_ORDER.filter((c) => meta.unlocked.includes(c));
    /*
     * CLASS ĐANG XEM — xem chú thích ở khai báo `ciClass`/`ciTab` đầu class (KHÔNG nằm trong save).
     */
    // Class đang chọn có thể đã bị bỏ mở khoá ⇒ luôn kẹp về một id HỢP LỆ.
    const current: ClassId = this.ciClass && ids.includes(this.ciClass) ? this.ciClass : ids[0];
    this.ciClass = current;
    const tab: 'stats' | 'skills' = this.ciTab === 'skills' ? 'skills' : 'stats';

    const n = (v: number): string => Math.round(v).toLocaleString(getLang() === 'en' ? 'en-US' : 'vi-VN');
    const num = (v: number, d = 2): string => v.toFixed(d);

    /**
     * KHỐI "TUYỆT CHIÊU" — nút vàng người chơi tự bấm khi đủ mana.
     *
     * ⚠️⚠️ LỖI ĐÃ DÍNH (người dùng: *"mục `?` mô tả tướng chưa có mô tả ult"*).
     * Bảng này ban đầu chỉ liệt kê `cls.skills` (7 tuyệt kỹ mở theo wave) mà QUÊN hẳn ULTIMATE —
     * vốn là cơ chế riêng, có dữ liệu riêng ở `data/ultimates.ts` (`ULTIMATES[classId]`), không nằm
     * trong `skills[]`. Đo được trước khi sửa: `document.querySelectorAll('.ci-ult').length === 0`.
     *
     * Vì sao phải tách bạch rõ: người chơi rất dễ nhầm "7 chiêu thức" với "tuyệt chiêu". Hai thứ
     * khác nhau về cách dùng (tuyệt chiêu bấm tay, 1 cái/ class, hồi 3 phút, có thời lượng) nên
     * khối này ghi rõ nhãn + thời lượng + hồi chiêu.
     */
    const ultBlock = (id: ClassId): string => {
      const def = ULTIMATES[id];
      if (!def) return '';
      /*
       * ⚠️ `duration: 0` KHÔNG phải "0 giây" — theo `UltimateDef.duration` nó nghĩa là **KHÔNG đếm
       * ngược** (ultimate của Đẻ mướn chỉ kết thúc khi con bị thu phục chết). Bản đầu tôi chỉ hỏi
       * `Number.isFinite(duration)` nên in ra "kéo dài 0s" — đo được trên bảng thật, sai nghĩa hẳn.
       * Điều kiện phải là `> 0` mới có thời lượng; `Infinity` và `0` đều là "không đếm ngược".
       */
      const dur =
        def.duration > 0
          ? t('{n}s', '{n}s', { n: def.duration })
          : t('không đếm ngược', 'no countdown');
      return `
        <div class="ci-ult">
          <div class="ci-ult-head">
            <span class="ci-ult-tag">${t('TUYỆT CHIÊU', 'ULTIMATE')}</span>
            <span class="ci-ult-name">${ultName(def)}</span>
            <span class="ci-ult-meta">${t('kéo dài', 'lasts')} ${dur} · ${t('hồi', 'cd')} ${def.cooldown}s</span>
          </div>
          <div class="ci-ult-desc">${ultDesc(def)}</div>
        </div>`;
    };

    // ------------------------------------------------------------------ BƯỚC 1: DẢI AVATAR
    /*
     * Người dùng: *"tôi muốn chỗ đó hiển thị avata của các class"*.
     *
     * Ảnh head lấy qua `classHeadHtml()` — CÙNG nguồn với "Chiêu mộ nhanh"/ Túi đồ (ô 1 của lưới
     * 3×3 trong `art/ai/<class>-parts.png`, đã cắt sẵn + tách nền magenta). Dùng lại hàm đó thay vì
     * tự dựng ảnh mới ⇒ không có bản sao thứ hai để lệch, và class chưa có art vẫn ra MẢNH GHÉP
     * đúng như mọi chỗ khác.
     *
     * ⚠️ Nút là `<button>` (không phải `<div>`): bàn phím + screen reader dùng được, và
     * `aria-pressed` cho biết đang chọn class nào.
     */
    const avatars = ids
      .map((id) => {
        const cls = CLASSES[id];
        const on = id === current;
        return `<button class="ci-av${on ? ' active' : ''}" data-ci-class="${id}"
            aria-pressed="${on}" title="${className(id)} — ${classRole(id)}"
            style="--ci-color:#${cls.color.toString(16).padStart(6, '0')}">
            ${classHeadHtml(id, 44, 'ci-av-img')}
            <span class="ci-av-name">${className(id)}</span>
          </button>`;
      })
      .join('');

    // ------------------------------------------------------------------ BƯỚC 2: 2 TAB
    const tabBtn = (key: 'stats' | 'skills', vi: string, en: string): string =>
      `<button class="ci-tab${tab === key ? ' active' : ''}" data-ci-tab="${key}" role="tab"
        aria-selected="${tab === key}">${t(vi, en)}</button>`;

    // ------------------------------------------------------------------ TAB "CHỈ SỐ"
    /**
     * `CLASSES[id].base` là số THÔ, `classStatsFor()` mới là số THẬT (đã gồm điểm nâng class).
     * Hiện delta khi người chơi đã nâng → thấy ngay công tập luyện của mình, thay vì chỉ thấy
     * một con số không biết từ đâu ra.
     */
    const statRow = (label: string, cur: number, base: number, suffix = '', d = 0): string => {
      const diff = cur - base;
      const show = Math.abs(diff) > (d > 0 ? 1e-6 : 0.5);
      const delta = show
        ? `<span class="ci-delta">+${(d > 0 ? diff : Math.round(diff)).toFixed(d)}</span>`
        : '';
      return `<div class="ci-stat"><span class="ci-stat-k">${label}</span>
        <span class="ci-stat-v">${(d > 0 ? cur.toFixed(d) : n(cur))}${suffix}${delta}</span></div>`;
    };

    const panel = (id: ClassId): string => {
      const cls = CLASSES[id];
      const st = classStatsFor(id, meta);
      const color = `#${cls.color.toString(16).padStart(6, '0')}`;

      if (tab === 'stats') {
        return `
          <div class="ci-panel" role="tabpanel">
            <p class="ci-blurb">${classBlurb(id)}</p>
            <div class="ci-stats">
              ${statRow('HP', st.hp, cls.base.hp)}
              ${statRow('ATK', st.atk, cls.base.atk)}
              ${statRow('DEF', st.def, cls.base.def)}
              ${statRow('Mana', st.mana, cls.base.mana)}
              ${statRow('Stamina', st.stamina, cls.base.stamina)}
              ${statRow(t('Tốc đánh', 'Atk speed'), st.atkSpeed, cls.base.atkSpeed, '/s', 1)}
              ${statRow(t('Tầm đánh', 'Range'), st.range, cls.base.range)}
              ${statRow(t('Tốc chạy', 'Move speed'), st.moveSpeed, cls.base.moveSpeed)}
              ${statRow(t('Né', 'Dodge'), st.dodge * 100, cls.base.dodge * 100, '%', 0)}
              ${statRow(t('Chí mạng', 'Crit'), st.crit * 100, cls.base.crit * 100, '%', 0)}
              ${statRow(t('Hồi Mana', 'Mana regen'), st.manaRegen, cls.base.manaRegen, '/s', 0)}
              ${statRow(t('Hồi Thể lực', 'Stamina regen'), st.staminaRegen, cls.base.staminaRegen, '/s', 0)}
            </div>
            <div class="ci-power">
              ${t('Lực chiến', 'Power')} <b>${n(powerScore(st))} LC</b>
              <span class="ci-power-note">${t('đã gồm điểm tập luyện', 'includes training')}</span>
            </div>
          </div>`;
      }

      const skills = cls.skills
        .map(
          (s, i) => `
          <li class="ci-skill">
            <div class="ci-skill-head">
              <span class="ci-skill-name">${i + 1}. ${skillName(s)}</span>
              <span class="ci-skill-meta">${t('Wave', 'Wave')} ${s.unlockWave} · ${s.manaCost} ${t('mana', 'mana')} · ${s.cooldown}s</span>
            </div>
            <div class="ci-skill-desc">${skillDesc(s)}</div>
          </li>`,
        )
        .join('');
      return `
        <div class="ci-panel" role="tabpanel">
          ${ultBlock(id)}
          <div class="ci-skills-label">${t(
            'Chiêu thức theo wave — mang tối đa {n} chiêu vào trận',
            'Wave skills — equip up to {n} for a run',
            { n: MAX_EQUIPPED_SKILLS },
          )}</div>
          <ul class="ci-skills">${skills}</ul>
        </div>`;
    };

    // ------------------------------------------------------------------ DỰNG BẢNG
    const cls = CLASSES[current];
    this.modalTitle.textContent = t('Võ Lâm Môn Phái', 'Classes & Skills');
    /*
     * ⚠️ ĐÃ BỎ KHỐI HƯỚNG DẪN `.ci-intro` (người dùng: *"Block này không cần"*).
     * Khối đó dài 5 dòng, chiếm chỗ ngay trên dải avatar và đẩy nội dung xuống — trong khi mọi thứ
     * nó giải thích đã HIỆN NGAY TRÊN GIAO DIỆN: 2 tab ghi rõ "Chỉ số" / "Chiêu thức", nhãn
     * "TUYỆT CHIÊU" nằm ngay trên danh sách chiêu, và "mang tối đa {n} chiêu" đã có ở
     * `.ci-skills-label`. Chữ giải thích lặp lại thứ người chơi đang nhìn thấy = chỉ tổ rối.
     * ⇒ ĐỪNG thêm lại. Nếu cần nói thêm gì thì nói NGAY TẠI chỗ đó, đừng dựng khối hướng dẫn chung.
     */
    this.modalBody.innerHTML = `
      <div class="ci-avatars" role="tablist" aria-label="${t('Chọn môn phái', 'Pick a class')}">${avatars}</div>
      <header class="ci-head" style="--ci-color:#${cls.color.toString(16).padStart(6, '0')}">
        <span class="ci-dot"></span>
        <div>
          <div class="ci-name">${className(current)} <em>${classTitle(current)}</em></div>
          <div class="ci-role">${classRole(current)}</div>
        </div>
      </header>
      <div class="ci-tabs" role="tablist">
        ${tabBtn('stats', 'Chỉ số', 'Stats')}
        ${tabBtn('skills', 'Chiêu thức', 'Skills')}
      </div>
      ${panel(current)}`;
    this.modal.classList.remove('hidden');

    /*
     * GẮN SỰ KIỆN SAU KHI ĐÃ BƠM HTML (`innerHTML` xoá sạch listener cũ).
     *
     * ⚠️ Mọi nút đều `this.render()` lại — KHÔNG tự ý đổi DOM bằng tay. Lý do: nhãn phải đổi theo
     * ngôn ngữ (`className()`/`t()` đọc ngôn ngữ LÚC GỌI), nên vẽ lại toàn bộ là cách duy nhất chắc
     * chắn không sót chỗ nào lệch ngôn ngữ.
     */
    this.modalBody.querySelectorAll<HTMLElement>('[data-ci-class]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        this.ciClass = b.dataset.ciClass as ClassId;
        this.showClasses();
      }),
    );
    this.modalBody.querySelectorAll<HTMLElement>('[data-ci-tab]').forEach((b) =>
      b.addEventListener('click', () => {
        sound.playClick();
        this.ciTab = b.dataset.ciTab === 'skills' ? 'skills' : 'stats';
        this.showClasses();
      }),
    );
  }
}
